import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  X,
  Plus,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Search,
  Filter,
  Info,
  Package,
  Tag,
  Printer,
  Download,
  MinusCircle,
  Zap,
  Usb,
  Laptop,
  RefreshCw,
  Settings,
  Trash2,
  Sparkles,
  Layers,
} from "lucide-react";
import BarcodeSVG from "../../utils/barcodeGenerator";
import { masterService } from "../../api/masterService";
import barcodeLabelService from "../../api/barcodeLabelService";
import conversionService from "../../api/conversionService";
import { useNotification } from "../../contexts/NotificationContext";
import SearchableSelect from "../SearchableSelect";
import ConfirmationModal from "../ConfirmationModal";
import jsPDF from "jspdf";
import { createPortal, flushSync } from "react-dom";
import { executeTsplPrint, getDirectPrinterStatus } from "../../utils/tsplLabelBuilder";
import { getSavedWindowsPrinter, setSavedWindowsPrinter, disconnectWindowsPrinter, listWindowsPrinters, isWindowsPrintBridgeAvailable } from "../../utils/windowsPrintBridge";


const PRESET_CATEGORIES = [
  {
    id: "thermal_multi",
    name: "Roll Thermal Multi-Kolom",
    badge: "Rekomendasi POS",
    desc: "Stiker roll 2-3 kolom untuk printer thermal desktop (Xprinter XP-420B, XP-365B, dsb)",
    items: [
      {
        id: "33x15",
        width: 33,
        height: 15,
        label: "33 × 15 mm",
        hint: "3 Kolom • Roll 108 mm",
        recommendedMedia: "thermal",
        recommendedRoll: "roll108",
        recommendedCols: 3,
        popular: true,
      },
      {
        id: "33x25",
        width: 33,
        height: 25,
        label: "33 × 25 mm",
        hint: "3 Kolom • Roll 108 mm",
        recommendedMedia: "thermal",
        recommendedRoll: "roll108",
        recommendedCols: 3,
        popular: true,
      },
      {
        id: "33x19",
        width: 33,
        height: 19,
        label: "33 × 19 mm",
        hint: "2 Kolom • Roll 72 mm",
        recommendedMedia: "thermal",
        recommendedRoll: "roll72",
        recommendedCols: 2,
        popular: true,
      },
      {
        id: "38x20",
        width: 38,
        height: 20,
        label: "38 × 20 mm",
        hint: "2 Kolom • Roll 80 mm",
        recommendedMedia: "thermal",
        recommendedRoll: "roll80",
        recommendedCols: 2,
        popular: true,
      },
      {
        id: "24x15",
        width: 24,
        height: 15,
        label: "24 × 15 mm",
        hint: "3 Kolom • Roll 80 mm",
        recommendedMedia: "thermal",
        recommendedRoll: "roll80",
        recommendedCols: 3,
      },
    ],
  },
  {
    id: "thermal_single",
    name: "Roll Thermal 1 Kolom",
    badge: "Standar Roll",
    desc: "Stiker 1 kolom untuk roll 58 mm, 80 mm, atau 108 mm",
    items: [
      {
        id: "40x30",
        width: 40,
        height: 30,
        label: "40 × 30 mm",
        hint: "1 Kolom (Roll 58/80 mm)",
        recommendedMedia: "thermal",
        recommendedRoll: "roll58",
        recommendedCols: 1,
        popular: true,
      },
      {
        id: "50x30",
        width: 50,
        height: 30,
        label: "50 × 30 mm",
        hint: "1 Kolom (Roll 80 mm)",
        recommendedMedia: "thermal",
        recommendedRoll: "roll80",
        recommendedCols: 1,
      },
      {
        id: "35x20",
        width: 35,
        height: 20,
        label: "35 × 20 mm",
        hint: "1 Kolom (Roll 58 mm)",
        recommendedMedia: "thermal",
        recommendedRoll: "roll58",
        recommendedCols: 1,
      },
      {
        id: "35x30",
        width: 35,
        height: 30,
        label: "35 × 30 mm",
        hint: "1 Kolom (Roll 58 mm)",
        recommendedMedia: "thermal",
        recommendedRoll: "roll58",
        recommendedCols: 1,
      },
      {
        id: "50x40",
        width: 50,
        height: 40,
        label: "50 × 40 mm",
        hint: "1 Kolom (Roll 80 mm)",
        recommendedMedia: "thermal",
        recommendedRoll: "roll80",
        recommendedCols: 1,
      },
      {
        id: "60x40",
        width: 60,
        height: 40,
        label: "60 × 40 mm",
        hint: "1 Kolom (Roll 80/108 mm)",
        recommendedMedia: "thermal",
        recommendedRoll: "roll80",
        recommendedCols: 1,
      },
      {
        id: "25x15",
        width: 25,
        height: 15,
        label: "25 × 15 mm",
        hint: "1 Kolom Mini (Roll 58 mm)",
        recommendedMedia: "thermal",
        recommendedRoll: "roll58",
        recommendedCols: 1,
      },
      {
        id: "30x20",
        width: 30,
        height: 20,
        label: "30 × 20 mm",
        hint: "1 Kolom (Roll 58 mm)",
        recommendedMedia: "thermal",
        recommendedRoll: "roll58",
        recommendedCols: 1,
      },
      {
        id: "100x150",
        width: 100,
        height: 150,
        label: "100 × 150 mm",
        hint: "Resi Paket (Roll 108 mm)",
        recommendedMedia: "thermal",
        recommendedRoll: "roll108",
        recommendedCols: 1,
      },
    ],
  },
];

const PRESET_SIZES = PRESET_CATEGORIES.flatMap((c) => c.items);

const PAPER_SIZES = [
  { id: "a4", label: "A4 (210 × 297 mm)", width: 210, height: 297 },
  { id: "letter", label: "Letter (216 × 279 mm)", width: 216, height: 279 },
  { id: "a5", label: "A5 (148 × 210 mm)", width: 148, height: 210 },
  { id: "f4", label: "F4 (210 × 330 mm)", width: 210, height: 330 },
];

const ROLL_SIZES = [
  { id: "roll108", label: "Roll 108 mm", width: 108 },
  { id: "roll80", label: "Roll 80 mm", width: 80 },
  { id: "roll72", label: "Roll 72 mm", width: 72 },
  { id: "roll58", label: "Roll 58 mm", width: 58 },
];

const MEDIA_TYPES = [
  { id: "sheet", label: "Kertas A4", desc: "Kertas HVS lembaran biasa, layout per halaman" },
  { id: "sticker", label: "Kertas Stiker Roll", desc: "Kertas stiker selembaran, garis potong putus-putus" },
  { id: "thermal", label: "Thermal Label Roll", desc: "Printer label thermal, kontinu tanpa pemecahan halaman" },
];

const GAP_MM = 2;
const MARGIN_MM = 1;
const MM_TO_PX = 96 / 25.4;
const MAX_THERMAL_PAGE_H = 400;
const BARCODE_LABEL_SETTINGS_KEY = "barcode_label_record_settings_v1";
const DEFAULT_DISPLAY_FIELDS = {
  show_product_name: true,
  show_price: true,
  show_sku: false,
  show_barcode: true,
};

function getTenantId() {
  try {
    return localStorage.getItem("tenantId") || "default";
  } catch {
    return "default";
  }
}

function getTenantStorageKey(baseKey) {
  return `${baseKey}_tenant_${getTenantId()}`;
}

function readCustomSizes() {
  try {
    const key = getTenantStorageKey("barcode_label_custom_sizes_v1");
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveCustomSizes(list) {
  try {
    const key = getTenantStorageKey("barcode_label_custom_sizes_v1");
    localStorage.setItem(key, JSON.stringify(list));
  } catch {}
}

function readBarcodeLabelSettings() {
  try {
    const key = getTenantStorageKey(BARCODE_LABEL_SETTINGS_KEY);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function getBarcodeLabelSettings(id) {
  if (!id) return null;
  return readBarcodeLabelSettings()[id] || null;
}

function saveBarcodeLabelSettings(id, settings) {
  if (!id) return;
  try {
    const allSettings = readBarcodeLabelSettings();
    allSettings[id] = settings;
    const key = getTenantStorageKey(BARCODE_LABEL_SETTINGS_KEY);
    localStorage.setItem(key, JSON.stringify(allSettings));
  } catch {
    // Pengaturan tetap aman di state selama sesi berjalan jika storage penuh.
  }
}

const getTextLimit = (cardW) => Math.max(6, Math.floor(cardW / 2));

const fitLabelText = (text, limit) => {
  const t = String(text || "");
  return t.length > limit ? t.slice(0, limit) : t;
};

const formatRupiah = (v) => `Rp${Number(v || 0).toLocaleString("id-ID")}`;

// Layout konten kartu label berskala dinamis berdasarkan ukuran kartu
function getCardLayout(W, H, fields) {
  const isSmall = H <= 25;
  const isThreeColumn = W === 33 && H === 15;
  
  // Padding presisi di semua sisi
  const padX = isThreeColumn ? 1.0 : isSmall ? 1.0 : 1.5;
  const padY = isThreeColumn ? 0.8 : isSmall ? 0.7 : 0.9;
  
  // Ukuran font proporsional, rata tengah, dan tidak kebesaran
  const nameSize = fields.show_product_name ? (isThreeColumn ? 2.0 : isSmall ? Math.max(1.8, H * 0.10) : H * 0.11) : 0;
  const priceSize = fields.show_price ? (isThreeColumn ? 1.8 : isSmall ? Math.max(1.6, H * 0.08) : H * 0.09) : 0;
  const smallSize = fields.show_sku ? (isThreeColumn ? 1.4 : isSmall ? Math.max(1.3, H * 0.055) : H * 0.065) : 0;

  const spacing1 = isThreeColumn ? 0.2 : H * 0.004; 
  const spacing2 = isThreeColumn ? 0.2 : H * 0.004; 

  let usedH = padY * 2;
  if (nameSize) usedH += nameSize * 1.15 + spacing1;
  if (priceSize) usedH += priceSize * 1.0 + spacing2;

  let barcodeH = 0;
  if (fields.show_barcode) {
    barcodeH = Math.max(isThreeColumn ? 5.6 : isSmall ? 4.5 : 6.0, H - usedH);
    if (isThreeColumn) barcodeH = Math.min(barcodeH, 6.4);
  }

  return {
    padX,
    padY,
    nameSize,
    priceSize,
    smallSize,
    barcodeH,
    spacing1,
    spacing2,
  };
}

// Kartu label tunggal konsisten yang digunakan di Step 1, Step 3 DOM, dan Print Portal
function LabelCard({ prod, fields, cardW, cardH, isThermal }) {
  const lay = getCardLayout(cardW, cardH, fields);
  const isSmall = cardH <= 25;
  const isThreeColumn = cardW === 33;
  const isTall = cardH >= 20;
  const combinePriceAndSku = isSmall && fields.show_price && fields.show_sku && prod.sku;

  return (
    <div
      style={{
        width: `${cardW}mm`,
        height: `${cardH}mm`,
        border: isThermal ? "0.2mm dashed #cbd5e1" : "0.25mm solid #cbd5e1",
        borderRadius: "2mm",
        padding: `${isTall ? 1.4 : lay.padY}mm ${lay.padX}mm`,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        textAlign: "center",
        background: "#ffffff",
        overflow: "hidden",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* 1. Nama Produk (Atas, Rata Tengah, Multi-Line Wrapping untuk Label Tinggi) */}
      {fields.show_product_name && (() => {
        let text = prod.name || "Nama Produk";
        if (prod.variantName && !text.toLowerCase().includes(prod.variantName.toLowerCase())) {
          const vStr = prod.variantName.trim();
          const formattedV = vStr.startsWith("(") ? vStr : `(${vStr})`;
          text = `${prod.parentName || text} ${formattedV}`;
        } else if (text.includes("\n")) {
          const parts = text.split("\n");
          const sub = parts[1].trim();
          const formattedSub = sub.startsWith("(") ? sub : `(${sub})`;
          text = `${parts[0].trim()} ${formattedSub}`;
        }

        const safeWidthMm = Math.max(10, cardW - 4);
        const maxChars = Math.max(6, Math.floor((safeWidthMm * 8) / 9.5));

        return (
          <div
            style={{
              fontSize: `${lay.nameSize}mm`,
              lineHeight: 1.15,
              fontWeight: 700,
              color: "#0f172a",
              width: "100%",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: isTall ? 2 : 1,
              WebkitBoxOrient: "vertical",
              paddingLeft: "0.5mm",
              paddingRight: "0.5mm",
              boxSizing: "border-box",
              flexShrink: 0,
            }}
            title={text}
          >
            {text}
          </div>
        );
      })()}

      {/* 2. Harga & SKU (Tengah, Rata Tengah, Proporsional) */}
      {combinePriceAndSku ? (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "center",
            gap: "1.5mm",
            width: "100%",
            lineHeight: 1,
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: `${lay.priceSize}mm`,
              fontWeight: 700,
              color: "#0f172a",
              whiteSpace: "nowrap",
              letterSpacing: "-0.2px",
            }}
          >
            {formatRupiah(prod.price || 0)}
          </span>
          <span
            style={{
              fontSize: `${lay.smallSize}mm`,
              fontFamily: "'Inter', monospace",
              fontWeight: 600,
              color: "#475569",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {prod.sku}
          </span>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", lineHeight: 1, textAlign: "center", flexShrink: 0 }}>
          {fields.show_price && (
            <div
              style={{
                fontSize: `${lay.priceSize}mm`,
                fontWeight: 700,
                color: "#0f172a",
                whiteSpace: "nowrap",
                letterSpacing: "-0.2px",
                textAlign: "center",
              }}
            >
              {formatRupiah(prod.price || 0)}
            </div>
          )}

          {fields.show_sku && prod.sku && (
            <div
              style={{
                fontSize: `${lay.smallSize}mm`,
                fontFamily: "'Inter', monospace",
                fontWeight: 600,
                color: "#475569",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginTop: fields.show_price ? "0.2mm" : "0",
                textAlign: "center",
              }}
            >
              {prod.sku}
            </div>
          )}
        </div>
      )}

      {/* 3. Barcode (Bawah, Rata Tengah, Mengisi Area Bawah Proporsional) */}
      {fields.show_barcode && (() => {
        const usedNonBarcodeH =
          (fields.show_product_name ? lay.nameSize * (isTall ? 2.2 : 1.2) : 0) +
          (fields.show_price ? lay.priceSize * 1.15 : 0) +
          (fields.show_sku && !combinePriceAndSku ? lay.smallSize * 1.1 : 0) +
          (isTall ? 2.8 : lay.padY * 2);
        
        const availableBarH_mm = Math.max(3.5, cardH - usedNonBarcodeH);
        const barcodeAreaW = Math.max(8, cardW - lay.padX * 2);
        const totalHeightPx = availableBarH_mm * MM_TO_PX;
        const fontSizePx = Math.max(5.5, Math.min(8.0, Math.floor(totalHeightPx * 0.20)));
        const barH = Math.max(8, Math.floor(totalHeightPx - fontSizePx - 3));

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            <BarcodeSVG
              value={prod.barcode || "8991234567001"}
              height={barH}
              maxWidth={barcodeAreaW * MM_TO_PX}
              showText={true}
              fontSize={fontSizePx}
            />
          </div>
        );
      })()}
    </div>
  );
}

function LabelSheetContent({ stickers, cols, cardW, cardH, gapX, gapY, marginX, marginY, fields, isThermal, fixedGridWidth }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, ${cardW}mm)`,
        columnGap: `${gapX}mm`,
        rowGap: `${gapY}mm`,
        width: fixedGridWidth ? `${fixedGridWidth}mm` : "100%",
        paddingTop: `${marginY}mm`,
        paddingBottom: `${marginY}mm`,
        paddingLeft: `${marginX}mm`,
        paddingRight: `${marginX}mm`,
        boxSizing: "border-box",
        justifyContent: "start",
        alignContent: "start",
      }}
    >
      {stickers.map((prod, idx) => (
        <div key={`${prod.id}-${idx}`} style={{ breakInside: "avoid", pageBreakInside: "avoid" }}>
          <LabelCard
            prod={prod}
            fields={fields}
            cardW={cardW}
            cardH={cardH}
            isThermal={isThermal}
          />
        </div>
      ))}
    </div>
  );
}

const CODE128_PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112"
];

function drawPdfBarcode(doc, text, x, y, width, height) {
  if (!text) text = "123456789";
  const codes = [104];
  let checksum = 104;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i) >= 32 && text.charCodeAt(i) <= 126 ? text.charCodeAt(i) - 32 : 0;
    codes.push(code);
    checksum += code * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(106);

  let patternStr = "";
  for (const c of codes) {
    patternStr += CODE128_PATTERNS[c] || "111111";
  }

  let totalModules = 0;
  for (let i = 0; i < patternStr.length; i++) {
    totalModules += parseInt(patternStr[i], 10);
  }

  const moduleWidth = width / totalModules;
  let currX = x;

  doc.setFillColor(0, 0, 0);
  for (let i = 0; i < patternStr.length; i++) {
    const modCount = parseInt(patternStr[i], 10);
    const modW = modCount * moduleWidth;
    if (i % 2 === 0) {
      doc.rect(currX, y, modW, height, "F");
    }
    currX += modW;
  }
}

export default function CreateBarcodeLabelModal({
  isOpen,
  onClose,
  onSuccess,
  editData = null,
  existingData = null,
}) {
  const { showSuccess, showError } = useNotification();
  const [step, setStep] = useState(1);

  // Step 1 State
  const [labelName, setLabelName] = useState("");
  const [selectedSizeId, setSelectedSizeId] = useState("33x15");
  const [customWidth, setCustomWidth] = useState(33);
  const [customHeight, setCustomHeight] = useState(15);
  const [customLabelName, setCustomLabelName] = useState("");
  const [isCustomSize, setIsCustomSize] = useState(false);
  const [activeSizeTab, setActiveSizeTab] = useState("preset"); // "preset" | "custom"
  const [customSizesList, setCustomSizesList] = useState(() => readCustomSizes());

  // Muat ulang daftar custom sizes khusus tenant akun yang aktif
  useEffect(() => {
    if (isOpen) {
      setCustomSizesList(readCustomSizes());
    }
  }, [isOpen]);

  const handleSelectPreset = (preset) => {
    setIsCustomSize(false);
    setSelectedSizeId(preset.id);
    if (preset.recommendedMedia) {
      setMediaCetak(preset.recommendedMedia);
    }
    if (preset.recommendedRoll) {
      setUkuranKertas(preset.recommendedRoll);
    }
  };

  const handleSelectCustomSize = (customItem) => {
    setIsCustomSize(true);
    setSelectedSizeId(customItem.id);
    setCustomWidth(customItem.width);
    setCustomHeight(customItem.height);
  };

  const handleAddCustomSize = () => {
    if (customWidth < 10 || customHeight < 10) {
      showError("Ukuran minimal label adalah 10 mm.");
      return;
    }
    const newId = `custom_${customWidth}x${customHeight}_${Date.now()}`;
    const cleanLabel = customLabelName.trim()
      ? `${customLabelName.trim()} (${customWidth} × ${customHeight} mm)`
      : `${customWidth} × ${customHeight} mm`;
    const newCustom = {
      id: newId,
      width: customWidth,
      height: customHeight,
      name: customLabelName.trim() || `Label ${customWidth}x${customHeight}`,
      label: cleanLabel,
      isCustom: true,
    };
    const updated = [newCustom, ...customSizesList.filter((s) => !(s.width === customWidth && s.height === customHeight))];
    setCustomSizesList(updated);
    saveCustomSizes(updated);
    setIsCustomSize(true);
    setSelectedSizeId(newId);
    showSuccess(`Ukuran kustom ${newCustom.label} tersimpan untuk akun ini.`);
    setCustomLabelName("");
  };

  const handleDeleteCustomSize = (id, e) => {
    if (e) e.stopPropagation();
    const updated = customSizesList.filter((s) => s.id !== id);
    setCustomSizesList(updated);
    saveCustomSizes(updated);
    if (selectedSizeId === id) {
      handleSelectPreset(PRESET_SIZES[0]);
    }
    showSuccess("Ukuran kustom dihapus.");
  };

  const [displayFields, setDisplayFields] = useState(() => ({
    ...DEFAULT_DISPLAY_FIELDS,
  }));

  const updateDisplayFields = (updater) => {
    setDisplayFields((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return { ...DEFAULT_DISPLAY_FIELDS, ...next };
    });
  };

  // Step 2 State: Products & Selection
  const PRODUCT_PAGE_SIZE = 10;
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [selectedProductMap, setSelectedProductMap] = useState({});
  const [productPage, setProductPage] = useState(1);
  const [allProductRows, setAllProductRows] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [filterOnlyBarcode, setFilterOnlyBarcode] = useState(false);
  const [categories, setCategories] = useState([]);
  const [generatingBarcodeId, setGeneratingBarcodeId] = useState(null);
  const pendingEditItemsRef = useRef(null);

  const [mediaCetak, setMediaCetak] = useState("thermal");
  const [ukuranKertas, setUkuranKertas] = useState("roll108");
  const [jumlahSalinan, setJumlahSalinan] = useState(1);
  const [sheetColumns, setSheetColumns] = useState(5); // Default ke 5 sesuai preferensi user
  const [zoomLevel, setZoomLevel] = useState("100%");
  const [showAllSizes, setShowAllSizes] = useState(false);
  const [checkingLabelName, setCheckingLabelName] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [recordSettingsReady, setRecordSettingsReady] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [isTestPrint, setIsTestPrint] = useState(false);
  const [directPrinterModalOpen, setDirectPrinterModalOpen] = useState(false);
  const [directPrinting, setDirectPrinting] = useState(false);
  const [savedDirectPrinter, setSavedDirectPrinter] = useState(null);
  const [windowsPrintersList, setWindowsPrintersList] = useState([]);
  const [loadingWindowsPrinters, setLoadingWindowsPrinters] = useState(false);
  const [bridgeStatus, setBridgeStatus] = useState(false);

  const refreshDirectPrinterStatus = async () => {
    try {
      const status = await getDirectPrinterStatus();
      setSavedDirectPrinter(status.usb || status.windows || status.serial || null);
      const bridgeOk = await isWindowsPrintBridgeAvailable();
      setBridgeStatus(bridgeOk);
    } catch (e) {
      console.warn("Direct printer check:", e);
    }
  };

  useEffect(() => {
    if (isOpen) {
      refreshDirectPrinterStatus();
    }
  }, [isOpen]);

  const handleOpenPrinterConfigModal = async () => {
    setDirectPrinterModalOpen(true);
    try {
      const bridgeOk = await isWindowsPrintBridgeAvailable();
      setBridgeStatus(bridgeOk);
      if (bridgeOk) {
        setLoadingWindowsPrinters(true);
        const list = await listWindowsPrinters();
        setWindowsPrintersList(list);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setLoadingWindowsPrinters(false);
    }
  };

  const handleConnectUsb = async () => {
    try {
      const saved = await requestUsbPrinter();
      setSavedDirectPrinter({ name: saved.productName || "Printer USB", ready: true });
      showSuccess("Printer USB berhasil disambungkan.");
      setDirectPrinterModalOpen(false);
      await handleDirectTsplPrint();
    } catch (err) {
      showError(err.message || "Gagal menyambungkan printer USB.");
    }
  };

  const handleSelectWindowsSpooler = async (printerName) => {
    try {
      const saved = setSavedWindowsPrinter(printerName);
      setSavedDirectPrinter({ name: saved.name, ready: true });
      showSuccess(`Printer ${printerName} dipilih via Windows Spooler.`);
      setDirectPrinterModalOpen(false);
      await handleDirectTsplPrint();
    } catch (err) {
      showError(err.message || "Gagal memilih printer Windows.");
    }
  };

  const handleDirectTsplPrint = async (targetRows = null) => {
    const rowsToPrint = targetRows || layout.thermalRows;
    if (!rowsToPrint || rowsToPrint.length === 0) {
      showError("Tidak ada label untuk dicetak.");
      return;
    }

    setDirectPrinting(true);
    try {
      const res = await executeTsplPrint({
        thermalRows: rowsToPrint,
        layout,
        fields: displayFields,
      });

      if (!targetRows) {
        await handleSaveRecord("Dicetak", false);
        showSuccess(`Berhasil mencetak ${rowsToPrint.length} baris label ke ${res.name}!`);
        if (onSuccess) onSuccess();
        onClose();
      } else {
        showSuccess(`Berhasil mencetak 1 baris cetak uji ke ${res.name}!`);
      }
    } catch (err) {
      if (err.message === "NO_PRINTER_CONFIGURED") {
        handleOpenPrinterConfigModal();
      } else {
        showError(err.message || "Gagal mencetak ke printer thermal.");
      }
    } finally {
      setDirectPrinting(false);
    }
  };
  const previewPanelRef = useRef(null);
  const [panelSize, setPanelSize] = useState({ w: 640, h: 560 });

  // Initialize Default Mock Data matching Gambar 3 if no products yet
  const initialMockProducts = [
    { id: "p1", name: "Kopi Arabica Premium", sku: "KRB-001", barcode: "8991234567001", price: 45000, initialQty: 6, defaultChecked: true },
    { id: "p2", name: "Gula Aren 250gr", sku: "GAR-015", barcode: "8991234567002", price: 18000, initialQty: 10, defaultChecked: true },
    { id: "p3", name: "Teh Melati Kotak", sku: "TMK-001", barcode: "8991234567003", price: 9500, initialQty: 8, defaultChecked: true },
    { id: "p4", name: "Susu Almond 1L", sku: "SAL-021", barcode: "8991234567004", price: 32000, initialQty: 1, defaultChecked: false },
    { id: "p5", name: "Tas Ransel Casual", sku: "TRC-110", barcode: "8991234567005", price: 150000, initialQty: 1, defaultChecked: false },
  ];

  useEffect(() => {
    if (isOpen) {
      setRecordSettingsReady(false);
      const dataToUse = existingData || editData;
      const savedRecordSettings = dataToUse?.id
        ? getBarcodeLabelSettings(dataToUse.id)
        : null;
      const persistedSettings = savedRecordSettings || dataToUse?.print_settings || {};

      setShowAllSizes(false);
      setZoomLevel(persistedSettings.zoom_level || "100%");
      setMediaCetak(persistedSettings.media_cetak || "thermal");
      setUkuranKertas(persistedSettings.ukuran_kertas || "roll108");
      setJumlahSalinan(Math.max(1, Number(persistedSettings.jumlah_salinan) || 1));
      setSheetColumns(Math.max(3, Number(persistedSettings.sheet_columns) || 5));

      if (dataToUse) {
        setStep(3); // Record lama langsung dibuka pada preview tanpa membuat record baru.
        setLabelName(dataToUse.name || "Label Barcode Pakaian");
        setDisplayFields({
          ...DEFAULT_DISPLAY_FIELDS,
          ...(dataToUse.display_fields || {}),
          ...(persistedSettings.display_fields || {}),
        });

        const storedCustomSize = persistedSettings.is_custom_size;
        const sizeStr = String(dataToUse.size || dataToUse.size_id || "33x15");
        const dimensions = sizeStr.match(/(\d+(?:\.\d+)?)\s*[×xX]\s*(\d+(?:\.\d+)?)/);
        const matchingPreset = PRESET_SIZES.find(
          (preset) =>
            sizeStr.includes(String(preset.width)) &&
            sizeStr.includes(String(preset.height)),
        );

        if (storedCustomSize || (!matchingPreset && dimensions)) {
          const width = Number(persistedSettings.custom_width || dimensions?.[1] || 33);
          const height = Number(persistedSettings.custom_height || dimensions?.[2] || 15);
          setIsCustomSize(true);
          setActiveSizeTab("custom");
          setCustomWidth(width);
          setCustomHeight(height);
        } else {
          setIsCustomSize(false);
          setActiveSizeTab("preset");
          setSelectedSizeId(
            persistedSettings.selected_size_id || matchingPreset?.id || "33x15",
          );
        }

        pendingEditItemsRef.current =
          dataToUse.items && Array.isArray(dataToUse.items) ? dataToUse.items : [];
        setSelectedProductMap(buildSelectionMapFromItems(pendingEditItemsRef.current, []));
        setRecordSettingsReady(true);
      } else {
        setStep(1);
        setLabelName("");
        setSelectedSizeId("33x15");
        setIsCustomSize(false);
        setCustomWidth(33);
        setCustomHeight(15);
        setActiveSizeTab("preset");
        setDisplayFields({ ...DEFAULT_DISPLAY_FIELDS });
        setMediaCetak("thermal");
        setUkuranKertas("roll108");
        setJumlahSalinan(1);
        setSheetColumns(5);
        setZoomLevel("100%");
        setSelectedProductMap({});
        pendingEditItemsRef.current = null;
        setRecordSettingsReady(false);
      }
      // Reset filter dan pencarian setiap modal dibuka.
      setProductSearch("");
      setFilterCategoryId("");
      setFilterOnlyBarcode(false);
      setShowFilterPanel(false);
      setProductPage(1);
      fetchProducts();
      fetchCategories();
    }
  }, [isOpen, editData, existingData]);

  // Expand produk + variannya jadi baris item yang bisa dipilih.
  // Varian dianggap item terpisah (retail mode) karena barcode/SKU biasanya
  // ada di varian, bukan di produk induk.
  // Satuan jual dari aturan konversi (mis. Lusin/Dus) juga jadi baris
  // terpisah karena punya barcode dan harga sendiri.
  const expandProductRows = (productsData, conversionRules = []) => {
    const rows = [];
    (productsData || []).forEach((p) => {
      const variants = Array.isArray(p.variants) ? p.variants : [];
      if (variants.length > 0) {
        variants.forEach((v) => {
          if (v.status && v.status !== "ACTIVE") return;
          rows.push({
            id: v.id,
            type: "variant",
            productId: p.id,
            name: v.name ? (v.name.startsWith("(") ? `${p.name} ${v.name}` : `${p.name} (${v.name})`) : p.name,
            parentName: p.name,
            variantName: v.name || "",
            sku: v.sku || "",
            barcode: v.barcode || "",
            price: v.sell_price || p.sell_price || 0,
            image_url: p.image_url,
            category_id: p.category_id,
          });
        });
      } else {
        rows.push({
          id: p.id,
          type: "product",
          productId: p.id,
          name: p.name,
          parentName: p.name,
          variantName: "",
          sku: p.sku || p.code || "",
          barcode: p.barcode || "",
          price: p.sell_price || 0,
          image_url: p.image_url,
          category_id: p.category_id,
        });
      }
    });

    // Baris satuan jual dari aturan konversi aktif
    const productMap = new Map((productsData || []).map((p) => [p.id, p]));
    (conversionRules || []).forEach((rule) => {
      if (rule.is_active === false) return;
      const p = productMap.get(rule.product_id);
      if (!p) return;
      const variantName = rule.variant_name || "";
      const unitName = rule.from_unit_name || "";
      if (!unitName) return;
      const baseName = variantName
        ? (variantName.startsWith("(") ? `${p.name} ${variantName}` : `${p.name} (${variantName})`)
        : p.name;
      rows.push({
        id: `unit_${rule.id}`,
        type: "unit",
        productId: p.id,
        variantId: rule.variant_id || null,
        unitId: rule.from_unit_id,
        unitName,
        ruleId: rule.id,
        conversionFactor: rule.conversion_factor,
        name: `${baseName} [${unitName}]`,
        parentName: baseName,
        variantName,
        sku: "",
        barcode: rule.barcode || "",
        price: rule.sell_price || 0,
        image_url: p.image_url,
        category_id: p.category_id,
      });
    });
    return rows;
  };

  const buildSelectionMapFromItems = (items, rows) => {
    const map = {};
    (items || []).forEach((it) => {
      let idKey;
      let matched;
      if (it.unit_id) {
        // Item satuan jual — cocokkan ke baris unit berdasarkan produk + varian + satuan
        matched = (rows || []).find(
          (r) =>
            r.type === "unit" &&
            r.productId === it.product_id &&
            r.unitId === it.unit_id &&
            (r.variantId || null) === (it.product_variant_id || null)
        );
        idKey = matched?.id || `unit_${it.product_id}_${it.product_variant_id || "base"}_${it.unit_id}`;
      } else {
        idKey = it.product_variant_id || it.product_id || it.id || `p_${Math.random()}`;
        matched = (rows || []).find((r) => r.id === idKey);
      }
      map[idKey] = {
        selected: true,
        qty: it.qty || 1,
        data: matched
          ? { ...matched, id: idKey }
          : {
              id: idKey,
              type: it.unit_id ? "unit" : it.product_variant_id ? "variant" : "product",
              productId: it.product_id,
              variantId: it.product_variant_id || null,
              unitId: it.unit_id || null,
              unitName: it.unit_name || "",
              name: it.name || it.product_name || "Produk",
              sku: it.sku || "",
              barcode: it.barcode || "",
              price: it.price || it.sell_price || 0,
              parentName: it.name || it.product_name || "Produk",
              variantName: "",
            },
      };
    });
    return map;
  };

  // Fetch API products (semua halaman) lalu expand jadi baris item
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const all = [];
      let page = 1;
      const pageSize = 500;
      let total = null;
      let safety = 0;
      do {
        const res = await masterService.products.list({ page, limit: pageSize });
        const items = res?.data || [];
        all.push(...items);
        total = res?.meta?.total ?? all.length;
        page += 1;
        safety += 1;
      } while (all.length < total && safety < 20);

      // Ambil semua aturan konversi sekali untuk baris satuan jual
      let conversionRules = [];
      try {
        const rulesRes = await conversionService.rules.list();
        conversionRules = rulesRes?.data?.items || rulesRes?.data?.data || rulesRes?.data || [];
        if (!Array.isArray(conversionRules)) conversionRules = [];
      } catch (err) {
        console.warn("Failed to fetch conversion rules:", err?.message);
      }

      const rows = all.length > 0 ? expandProductRows(all, conversionRules) : initialMockProducts;
      setAllProductRows(rows);

      if (pendingEditItemsRef.current?.length) {
        setSelectedProductMap(buildSelectionMapFromItems(pendingEditItemsRef.current, rows));
        pendingEditItemsRef.current = null;
      }
    } catch (err) {
      console.warn("Failed to fetch products, fallback to mock:", err?.message);
      setAllProductRows(initialMockProducts);
      if (pendingEditItemsRef.current?.length) {
        setSelectedProductMap(buildSelectionMapFromItems(pendingEditItemsRef.current, initialMockProducts));
        pendingEditItemsRef.current = null;
      }
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await masterService.categories.listAll();
      setCategories(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      console.warn("Failed to fetch categories:", err?.message);
    }
  };

  // Filter + paginasi murni di sisi klien agar search, kategori, dan
  // varian terpilih semuanya konsisten dalam satu daftar
  const filteredProductRows = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return allProductRows.filter((row) => {
      if (filterOnlyBarcode && !row.barcode) return false;
      if (filterCategoryId && String(row.category_id) !== String(filterCategoryId)) return false;
      if (q) {
        const hay = `${row.name} ${row.sku} ${row.barcode}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allProductRows, productSearch, filterOnlyBarcode, filterCategoryId]);

  const visibleProducts = useMemo(() => {
    const start = (productPage - 1) * PRODUCT_PAGE_SIZE;
    return filteredProductRows.slice(start, start + PRODUCT_PAGE_SIZE);
  }, [filteredProductRows, productPage]);

  // Reset ke halaman 1 setiap search / filter berubah
  useEffect(() => {
    if (!isOpen) return;
    setProductPage(1);
  }, [productSearch, filterOnlyBarcode, filterCategoryId]);

  // Reset halaman saat jumlah data berubah
  useEffect(() => {
    if (!isOpen) return;
    setProductPage((p) => Math.max(1, Math.min(p, Math.ceil(filteredProductRows.length / PRODUCT_PAGE_SIZE))));
  }, [filteredProductRows.length]);

  // Selected Size Object
  const activeSizeObj = useMemo(() => {
    if (isCustomSize) {
      const savedCustom = customSizesList.find((s) => s.id === selectedSizeId);
      if (savedCustom) return savedCustom;
      return { width: customWidth, height: customHeight, label: `${customWidth} × ${customHeight} mm` };
    }
    return PRESET_SIZES.find((s) => s.id === selectedSizeId) || PRESET_SIZES[0];
  }, [isCustomSize, selectedSizeId, customSizesList, customWidth, customHeight]);

  const previewScale = useMemo(() => {
    const w = activeSizeObj.width || 33;
    const h = activeSizeObj.height || 15;
    const maxW = 240;
    const maxH = 150;
    const rawW = w * MM_TO_PX;
    const rawH = h * MM_TO_PX;
    return Math.min(2.0, Math.max(0.6, Math.min(maxW / rawW, maxH / rawH)));
  }, [activeSizeObj]);

  const visiblePresetSizes = useMemo(() => {
    if (showAllSizes) return PRESET_SIZES;
    const compact = PRESET_SIZES.slice(0, 4);
    const selected = PRESET_SIZES.find((preset) => preset.id === selectedSizeId);
    return selected && !compact.some((preset) => preset.id === selected.id)
      ? [selected, ...compact]
      : compact;
  }, [showAllSizes, selectedSizeId]);

  // Selected Products Array
  const selectedProductsList = useMemo(() => {
    return Object.values(selectedProductMap).filter((item) => item.selected && item.qty > 0);
  }, [selectedProductMap]);

  const totalProductCount = selectedProductsList.length;
  const totalLabelCount = useMemo(() => {
    return selectedProductsList.reduce((sum, item) => sum + (item.qty || 0), 0);
  }, [selectedProductsList]);

  // Dynamic Columns & Rows per A4 Page (190mm printable width, 270mm printable height)
  // Flattened array of stickers for sheet layout
  const allStickersList = useMemo(() => {
    const list = [];
    selectedProductsList.forEach((item) => {
      for (let k = 0; k < item.qty * jumlahSalinan; k++) {
        list.push({ ...item.data });
      }
    });
    return list;
  }, [selectedProductsList, jumlahSalinan]);

  const isThermal = mediaCetak === "thermal";

  const activePaper = useMemo(() => {
    if (isThermal) {
      return ROLL_SIZES.find((s) => s.id === ukuranKertas) || ROLL_SIZES[0];
    }
    return PAPER_SIZES.find((s) => s.id === ukuranKertas) || PAPER_SIZES[0];
  }, [isThermal, ukuranKertas]);

  // Layout dinamis satu-satunya sumber kebenaran untuk preview, print, dan PDF
  const layout = useMemo(() => {
    const cardW = activeSizeObj.width || 33;
    const cardH = activeSizeObj.height || 15;
    const paperW = activePaper.width;
    
    // Hitung batas maksimal kolom agar tidak buyar saat ganti ukuran kertas
    const maxCols = Math.max(1, Math.floor(paperW / cardW));
    
    // Gunakan pilihan user, tapi batasi ke maxCols agar tidak melebihi kertas
    let cols = isThermal ? maxCols : Math.min(sheetColumns, maxCols);
    
    let gapX, gapY, marginX, marginY;

    if (isThermal) {
      const isThreeColumnRoll = cardW === 33 && paperW === 108;
      const isTwoColumnRoll72 = cardW === 33 && paperW === 72;
      const isTwoColumnRoll80 = cardW === 38 && paperW === 80;

      if (isThreeColumnRoll) {
        cols = 3;
        gapX = 2.0;
        gapY = 2.0;
        // Total lebar 3 label + 2 gap: 3 * 33 + 2 * 2 = 103mm
        // Margin kiri/kanan roll 108mm: (108 - 103) / 2 = 2.5mm
        marginX = 2.5;
        marginY = 0;
      } else if (isTwoColumnRoll72) {
        cols = 2;
        gapX = 2.0;
        gapY = 2.0;
        // Total lebar 2 label + 1 gap: 2 * 33 + 2 = 68mm
        // Margin kiri/kanan roll 72mm: (72 - 68) / 2 = 2.0mm
        marginX = 2.0;
        marginY = 0;
      } else if (isTwoColumnRoll80) {
        cols = 2;
        gapX = 1.0;
        gapY = 2.0;
        // Total lebar 2 label + 1 gap: 2 * 38 + 1 = 77mm
        // Margin kiri/kanan roll 80mm: (80 - 77) / 2 = 1.5mm
        marginX = 1.5;
        marginY = 0;
      } else {
        gapX = cols > 1 ? 2.0 : 0;
        gapY = 2.0;
        const totalGridW = cols * cardW + (cols - 1) * gapX;
        marginX = Math.max(0, (paperW - totalGridW) / 2);
        marginY = 0;
      }
    } else {
      // Margin kertas lembaran dibuat mepet (3mm)
      marginX = 3;
      marginY = 3;

      // Sisa ruang dibagi merata menjadi celah (gap) antar stiker
      const availableW = paperW - (marginX * 2);
      const totalCardW = cols * cardW;
      
      gapX = cols > 1 ? Math.max(0, (availableW - totalCardW) / (cols - 1)) : 3;
      gapY = 3; 
    }

    const printableH = isThermal
      ? MAX_THERMAL_PAGE_H - marginY * 2
      : Math.max(1, activePaper.height - marginY * 2);
    
    const rows = Math.max(1, Math.floor((printableH + gapY) / (cardH + gapY)));

    const totalCount = allStickersList.length;

    // Thermal rows chunking (setiap baris berisi 'cols' stiker)
    const thermalRows = [];
    for (let i = 0; i < totalCount; i += cols) {
      thermalRows.push(allStickersList.slice(i, i + cols));
    }

    const capacity = isThermal ? Math.max(1, totalCount) : cols * rows;
    const totalPages = Math.max(1, Math.ceil(totalCount / capacity));

    const pages = [];
    const pageHeights = [];
    for (let p = 0; p < totalPages; p++) {
      const stickers = allStickersList.slice(p * capacity, (p + 1) * capacity);
      pages.push(stickers);
      if (isThermal) {
        const filledRows = Math.max(1, Math.ceil(stickers.length / cols));
        pageHeights.push(marginY * 2 + filledRows * cardH + Math.max(0, filledRows - 1) * gapY);
      } else {
        pageHeights.push(activePaper.height);
      }
    }

    const paperH = isThermal
      ? pageHeights[previewPage - 1] ?? pageHeights[0]
      : activePaper.height;

    return {
      cardW,
      cardH,
      paperW,
      paperH,
      pageHeights,
      cols,
      rows,
      gapX,
      gapY,
      marginX,
      marginY,
      capacity,
      totalPages,
      pages,
      thermalRows,
      fitsW: cardW <= paperW,
      fitsH: cardH <= paperH,
    };
  }, [activeSizeObj, activePaper, isThermal, totalLabelCount, jumlahSalinan, allStickersList, sheetColumns, previewPage]);

  // Ukur panel preview biar sheet ke-scale pas, sama persis proporsinya dengan hasil cetak
  useEffect(() => {
    if (!isOpen || step !== 3) return;
    const el = previewPanelRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setPanelSize({ w: rect.width, h: rect.height });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, step]);

  // Jaga previewPage tetap dalam rentang saat jumlah halaman berubah
  useEffect(() => {
    setPreviewPage((p) => Math.max(1, Math.min(p, layout.totalPages)));
  }, [layout.totalPages]);

  const zoomPct = useMemo(() => {
    const z = { "100%": 1, "75%": 0.75, "50%": 0.5 }[zoomLevel] || 1;
    return z;
  }, [zoomLevel]);

  const scaleFactor = useMemo(() => {
    const sheetW = layout.paperW * MM_TO_PX;
    const sheetH = layout.paperH * MM_TO_PX;
    const availW = Math.max(50, panelSize.w - 32);
    const availH = Math.max(50, panelSize.h - 32);
    const fit = Math.min(availW / sheetW, availH / sheetH, 1);
    return Math.max(0.1, fit * zoomPct);
  }, [layout.paperW, layout.paperH, panelSize, zoomPct]);

  const currentPageStickers = layout.pages[previewPage - 1] || [];

  // Handlers
  const toggleSelectProduct = (product) => {
    setSelectedProductMap((prev) => {
      const next = { ...prev };
      const current = next[product.id];
      if (current && current.selected) {
        delete next[product.id];
      } else {
        next[product.id] = { selected: true, qty: current?.qty || 1, data: product };
      }
      return next;
    });
  };

  const updateProductQty = (product, delta) => {
    setSelectedProductMap((prev) => {
      const next = { ...prev };
      const current = next[product.id] || { selected: true, qty: 1, data: product };
      const newQty = Math.max(1, (current.qty || 1) + delta);
      next[product.id] = { selected: true, qty: newQty, data: product };
      return next;
    });
  };

  const selectableProductRows = useMemo(
    () => filteredProductRows.filter((product) => product.barcode),
    [filteredProductRows],
  );

  const isAllProductsSelected = useMemo(() => {
    if (selectableProductRows.length === 0) return false;
    return selectableProductRows.every((product) => selectedProductMap[product.id]?.selected);
  }, [selectableProductRows, selectedProductMap]);

  const toggleSelectAllProducts = () => {
    if (isAllProductsSelected) {
      setSelectedProductMap((prev) => {
        const next = { ...prev };
        selectableProductRows.forEach((product) => {
          delete next[product.id];
        });
        return next;
      });
      return;
    }

    setSelectedProductMap((prev) => {
      const next = { ...prev };
      selectableProductRows.forEach((product) => {
        next[product.id] = {
          selected: true,
          qty: next[product.id]?.qty || 1,
          data: product,
        };
      });
      return next;
    });
  };

  // Generate barcode EAN-13 (13 digit, prefix GS1 Indonesia 899)
  // Angka dibuat acak, lalu dicek ulang terhadap semua barcode yang sudah ada
  // di tenant ini agar dijamin tidak ada yang sama
  const generateEAN13 = () => {
    const prefix = "899";
    let body = "";
    for (let i = 0; i < 9; i++) body += Math.floor(Math.random() * 10);
    const twelve = (prefix + body).split("").map(Number);
    let sum = 0;
    twelve.forEach((d, i) => {
      sum += d * (i % 2 === 0 ? 1 : 3);
    });
    const check = (10 - (sum % 10)) % 10;
    return prefix + body + check;
  };

  // Barcode yang baru dibuat dalam satu sesi, agar antar item juga tidak bentrok
  const generatedBarcodesRef = useRef(new Set());

  const generateUniqueBarcode = () => {
    const existing = new Set(
      allProductRows.map((r) => r.barcode).filter(Boolean)
    );
    generatedBarcodesRef.current.forEach((b) => existing.add(b));
    for (let attempt = 0; attempt < 50; attempt++) {
      const barcode = generateEAN13();
      if (!existing.has(barcode)) {
        generatedBarcodesRef.current.add(barcode);
        return barcode;
      }
    }
    return null;
  };

  const saveBarcodeToBackend = async (row, newBarcode) => {
    if (row.type === "unit") {
      // Barcode satuan jual disimpan di aturan konversi
      await conversionService.rules.update(row.ruleId, {
        conversion_factor: Number(row.conversionFactor) || 1,
        barcode: newBarcode,
      });
      return;
    }
    if (row.type === "variant") {
      const { data: product } = await masterService.products.get(row.productId);
      const variant = (product.variants || []).find((v) => v.id === row.id);
      if (!variant) throw new Error("Varian tidak ditemukan");
      await masterService.products.variants.update(row.productId, row.id, {
        name: variant.name,
        sku: variant.sku || "",
        barcode: newBarcode,
        sell_price: variant.sell_price || 0,
        purchase_price: variant.purchase_price || 0,
        brand_id: variant.brand_id,
        unit_id: variant.unit_id,
        prep_time_mins: variant.prep_time_mins,
      });
    } else {
      const { data: product } = await masterService.products.get(row.productId);
      await masterService.products.update(row.productId, {
        ...product,
        category_id: product.category_id || undefined,
        unit_id: product.unit_id || undefined,
        barcode: newBarcode,
      });
    }
  };

  const handleGenerateBarcode = async (row) => {
    if (generatingBarcodeId) return;
    setGeneratingBarcodeId(row.id);
    try {
      const newBarcode = generateUniqueBarcode();
      if (!newBarcode) throw new Error("Gagal membuat barcode unik, coba lagi");
      await saveBarcodeToBackend(row, newBarcode);
      setAllProductRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, barcode: newBarcode } : r)));
      showSuccess(`Barcode ${newBarcode} berhasil dibuat untuk ${row.name.split("\n")[0]}`);
    } catch (err) {
      showError(err?.message || "Gagal membuat barcode, coba lagi");
    } finally {
      setGeneratingBarcodeId(null);
    }
  };

  // Generate barcode sekaligus untuk semua item yang belum punya barcode
  const itemsWithoutBarcode = useMemo(
    () => allProductRows.filter((r) => !r.barcode),
    [allProductRows]
  );
  const [confirmBulkGenerate, setConfirmBulkGenerate] = useState(false);
  const [bulkGenerateState, setBulkGenerateState] = useState({ active: false, done: 0, total: 0 });

  const handleBulkGenerateBarcode = async () => {
    const targets = allProductRows.filter((r) => !r.barcode);
    if (targets.length === 0) {
      setConfirmBulkGenerate(false);
      return;
    }
    setBulkGenerateState({ active: true, done: 0, total: targets.length });
    let successCount = 0;
    for (let i = 0; i < targets.length; i++) {
      const row = targets[i];
      try {
        const newBarcode = generateUniqueBarcode();
        if (!newBarcode) throw new Error("gagal");
        await saveBarcodeToBackend(row, newBarcode);
        setAllProductRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, barcode: newBarcode } : r)));
        successCount += 1;
      } catch (err) {
        console.warn("Gagal generate barcode untuk", row.name, err?.message);
      }
      setBulkGenerateState({ active: true, done: i + 1, total: targets.length });
    }
    setBulkGenerateState({ active: false, done: targets.length, total: targets.length });
    setConfirmBulkGenerate(false);
    if (successCount === targets.length) {
      showSuccess(`Berhasil membuat barcode untuk ${successCount} item`);
    } else {
      showError(`${successCount} dari ${targets.length} item berhasil dibuat barcode-nya`);
    }
  };

  // Step Validation
  const normalizeLabelName = (value) =>
    String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

  const validateLabelName = async () => {
    const normalizedName = normalizeLabelName(labelName);
    if (!normalizedName) {
      showError("Nama label tidak boleh kosong");
      return false;
    }

    setCheckingLabelName(true);
    try {
      const labels = await barcodeLabelService.listAll();
      const duplicate = labels.some(
        (label) =>
          label.id !== (existingData?.id || editData?.id) &&
          normalizeLabelName(label.name) === normalizedName,
      );
      if (duplicate) {
        showError("Nama label sudah digunakan. Gunakan nama lain.");
        return false;
      }
      return true;
    } catch (err) {
      showError("Nama label belum dapat diperiksa. Coba lagi.");
      return false;
    } finally {
      setCheckingLabelName(false);
    }
  };

  const handleNextStep1 = async () => {
    if (checkingLabelName) return;
    if (!(await validateLabelName())) return;
    setStep(2);
  };

  const handleNextStep2 = () => {
    if (totalProductCount === 0) {
      showError("Pilih minimal 1 produk untuk dicetak");
      return;
    }
    setStep(3);
  };

  const getCurrentRecordSettings = () => ({
    display_fields: { ...DEFAULT_DISPLAY_FIELDS, ...displayFields },
    media_cetak: mediaCetak,
    ukuran_kertas: ukuranKertas,
    jumlah_salinan: jumlahSalinan,
    sheet_columns: sheetColumns,
    zoom_level: zoomLevel,
    selected_size_id: selectedSizeId,
    is_custom_size: isCustomSize,
    custom_width: customWidth,
    custom_height: customHeight,
  });

  const activeRecordId = existingData?.id || editData?.id;
  useEffect(() => {
    if (!isOpen || !activeRecordId || !recordSettingsReady) return;
    saveBarcodeLabelSettings(activeRecordId, getCurrentRecordSettings());
  }, [
    isOpen,
    activeRecordId,
    recordSettingsReady,
    displayFields,
    mediaCetak,
    ukuranKertas,
    jumlahSalinan,
    sheetColumns,
    zoomLevel,
    selectedSizeId,
    isCustomSize,
    customWidth,
    customHeight,
  ]);

  // Save record baru atau perbarui status record lama tanpa membuat duplikat.
  const handleSaveRecord = async (status = "Disusun", keepOpen = false) => {
    if (savingRecord) return false;
    setSavingRecord(true);
    try {
      const recordSettings = getCurrentRecordSettings();
      const payload = {
        name: labelName.trim(),
        size: activeSizeObj.label,
        product_count: totalProductCount,
        total_labels: totalLabelCount,
        status,
        display_fields: recordSettings.display_fields,
        print_settings: recordSettings,
        items: selectedProductsList.map((i) => ({
          product_id: i.data.type === "product" ? i.data.id : i.data.productId,
          product_variant_id: i.data.type === "variant" ? i.data.id : i.data.variantId || undefined,
          unit_id: i.data.unitId || undefined,
          unit_name: i.data.unitName || undefined,
          name: i.data.name,
          sku: i.data.sku || "",
          barcode: i.data.barcode || "",
          price: i.data.price || i.data.sell_price || 0,
          qty: i.qty,
        })),
      };

      const existingId = existingData?.id || editData?.id;
      let savedRecord = null;
      if (existingId) {
        // Selalu kirim status terbaru agar record lama mengikuti aksi terakhir.
        await barcodeLabelService.updateStatus(existingId, status);
        saveBarcodeLabelSettings(existingId, recordSettings);
        savedRecord = { ...existingData, ...payload, id: existingId };
        showSuccess(
          status === "Dicetak"
            ? "Pengaturan label diperbarui dan siap dicetak"
            : "Pengaturan label diperbarui",
        );
      } else {
        savedRecord = await barcodeLabelService.create(payload);
        if (savedRecord?.id) {
          saveBarcodeLabelSettings(savedRecord.id, recordSettings);
        }
        showSuccess(
          status === "Dicetak"
            ? "Label barcode berhasil disimpan dan siap dicetak"
            : "Label barcode berhasil disimpan ke draf",
        );
      }

      if (!keepOpen) {
        if (onSuccess) onSuccess(savedRecord);
        onClose();
      }
      return true;
    } catch (err) {
      showError(err?.message || "Gagal menyimpan label barcode");
      return false;
    } finally {
      setSavingRecord(false);
    }
  };

  const handleSaveDraft = () => {
    handleSaveRecord("Disusun");
  };

  // Modal tetap terbuka selama print agar lembar cetak masih berada di DOM.
  const handlePrint = async () => {
    const saved = await handleSaveRecord("Dicetak", true);
    if (!saved) return;
    window.print();
    if (onSuccess) onSuccess();
    onClose();
  };

  // Test Print Handler, hanya halaman pertama yang ikut dicetak
  const handleTestPrint = () => {
    flushSync(() => setIsTestPrint(true));
    showSuccess("Mencetak 1 lembar cetak uji...");
    window.print();
    setIsTestPrint(false);
  };

  // Download PDF Handler telah dihapus karena jsPDF tidak dapat menyalin tata letak CSS Grid & Flexbox secara presisi.
  // Pengguna diarahkan menggunakan window.print() (Cetak Label) yang memiliki fitur asli "Simpan sebagai PDF" bawaan browser.


  if (!isOpen) return null;

  return (
    <>
      <style>{`
        ${
          isThermal
            ? `@page { size: ${layout.paperW}mm ${layout.cardH + layout.gapY}mm; margin: 0mm; }`
            : layout.pageHeights
                .map((h, i) => `@page sheet-p${i} { size: ${layout.paperW}mm ${h}mm; margin: 0mm; }`)
                .join("\n")
        }
        ${
          !isThermal
            ? layout.pageHeights
                .map((_, i) => `.print-sheet-p${i} { page: sheet-p${i}; }`)
                .join("\n")
            : ""
        }
        @media print {
          html, body {
            margin: 0mm !important;
            padding: 0mm !important;
            background: white !important;
          }
          body > *:not(.print-sheets) {
            display: none !important;
          }
          .print-sheets {
            display: block !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            background: white !important;
          }
          .printable-sheet {
            break-after: ${isThermal ? "always" : "page"} !important;
            page-break-after: always !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            margin: 0 auto !important;
            background: white !important;
            overflow: hidden !important;
          }
          
          .thermal-print-row {
            width: ${layout.paperW}mm !important;
            height: ${layout.cardH + layout.gapY}mm !important;
            box-sizing: border-box !important;
            padding-top: 0mm !important;
            padding-bottom: ${layout.gapY}mm !important;
            padding-left: ${layout.marginX}mm !important;
            padding-right: ${layout.marginX}mm !important;
            display: flex !important;
            flex-direction: row !important;
            gap: ${layout.gapX}mm !important;
            justify-content: flex-start !important;
            align-items: flex-start !important;
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            overflow: hidden !important;
            background: white !important;
          }
          .printable-sheet:last-child,
          .thermal-print-row:last-child {
            break-after: auto !important;
            page-break-after: auto !important;
          }
          @page {
            size: ${isThermal ? `${layout.paperW}mm ${layout.cardH + layout.gapY}mm` : `${layout.paperW}mm ${layout.paperH}mm`};
            margin: 0mm !important;
          }
        }
      `}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col my-auto max-h-[95vh]">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between bg-white sticky top-0 z-20">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Buat Label Barcode</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {step === 1 && "Lengkapi informasi label sebelum memilih produk."}
              {step === 2 && "Pilih produk dan tentukan jumlah label yang akan dibuat."}
              {step === 3 && "Periksa tampilan label sebelum mengunduh atau mencetak."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stepper Navigation */}
        <div className="px-6 py-4 bg-white border-b border-slate-200 flex items-center justify-between gap-2">
          {/* Step 1 */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= 1
                  ? "bg-teal-500 text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              {step > 1 ? <Check className="w-3.5 h-3.5" /> : "1"}
            </div>
            <span
              className={`text-[13px] font-bold ${
                step >= 1 ? "text-teal-600" : "text-slate-400"
              }`}
            >
              Informasi Label
            </span>
          </div>

          <div
            className={`flex-1 h-[2px] mx-3 transition-colors duration-200 ${
              step > 1 ? "bg-teal-500" : "bg-slate-200"
            }`}
          />

          {/* Step 2 */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= 2
                  ? "bg-teal-500 text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              {step > 2 ? <Check className="w-3.5 h-3.5" /> : "2"}
            </div>
            <span
              className={`text-[13px] font-bold ${
                step >= 2 ? "text-teal-600" : "text-slate-400"
              }`}
            >
              Pilih Produk
            </span>
          </div>

          <div
            className={`flex-1 h-[2px] mx-3 transition-colors duration-200 ${
              step > 2 ? "bg-teal-500" : "bg-slate-200"
            }`}
          />

          {/* Step 3 */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= 3
                  ? "bg-teal-500 text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              3
            </div>
            <span
              className={`text-[13px] font-bold ${
                step >= 3 ? "text-teal-600" : "text-slate-400"
              }`}
            >
              Preview & Cetak
            </span>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* STEP 1: INFORMASI & UKURAN LABEL */}
          {step === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Form Inputs Left Side */}
              <div className="lg:col-span-7 space-y-5">
                {/* Nama Label */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Nama Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={labelName}
                    onChange={(e) => setLabelName(e.target.value)}
                    placeholder="Contoh: Label Barcode Produk Toko"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-all placeholder:text-slate-400"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Nama digunakan untuk mengenali riwayat label barcode ini.
                  </p>
                </div>

                {/* Ukuran Label - Terstruktur & Terpisah Rapi */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-[13px] font-bold text-slate-800">
                      Ukuran Label <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200">
                      <button
                        type="button"
                        onClick={() => setActiveSizeTab("preset")}
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                          activeSizeTab === "preset"
                            ? "bg-white text-teal-700 shadow-sm"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        Ukuran Bawaan ({PRESET_SIZES.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSizeTab("custom")}
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all flex items-center gap-1 ${
                          activeSizeTab === "custom"
                            ? "bg-white text-indigo-700 shadow-sm"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        <span>Ukuran Kustom</span>
                        {customSizesList.length > 0 && (
                          <span className="w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 text-[10px] flex items-center justify-center font-bold">
                            {customSizesList.length}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* TAB 1: PRESET BAWAAN DENGAN KATEGORI JELAS */}
                  {activeSizeTab === "preset" && (
                    <div className="space-y-4">
                      {PRESET_CATEGORIES.map((category) => (
                        <div key={category.id} className="p-3.5 border border-slate-200/80 rounded-xl bg-slate-50/50 space-y-2.5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-slate-800">{category.name}</span>
                              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-teal-100 text-teal-800">
                                {category.badge}
                              </span>
                            </div>
                            <span className="text-[11px] text-slate-400">{category.items.length} Ukuran</span>
                          </div>
                          <p className="text-[11px] text-slate-500 leading-snug">{category.desc}</p>

                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-1">
                            {category.items.map((preset) => {
                              const isActive = !isCustomSize && selectedSizeId === preset.id;
                              return (
                                <div
                                  key={preset.id}
                                  onClick={() => handleSelectPreset(preset)}
                                  className={`relative p-2.5 rounded-xl border text-center cursor-pointer transition-all flex flex-col justify-center items-center min-h-[62px] ${
                                    isActive
                                      ? "border-teal-500 text-teal-900 ring-2 ring-teal-500/20 bg-teal-50/40 shadow-sm"
                                      : "border-slate-200 hover:border-slate-300 bg-white text-slate-700 hover:shadow-sm"
                                  }`}
                                >
                                  {isActive && (
                                    <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-teal-500 text-white flex items-center justify-center shadow-sm">
                                      <Check className="w-2.5 h-2.5" />
                                    </div>
                                  )}
                                  <div className="text-xs font-bold">{preset.label}</div>
                                  {preset.hint && (
                                    <div className="text-[10px] text-teal-600 font-semibold mt-0.5 leading-tight">
                                      {preset.hint}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* TAB 2: UKURAN KUSTOM (TERISOLASI PER AKUN) */}
                  {activeSizeTab === "custom" && (
                    <div className="space-y-4">
                      {/* Form Buat Ukuran Kustom Baru */}
                      <div className="p-4 border border-indigo-200 rounded-xl bg-indigo-50/30 space-y-3">
                        <div className="flex items-center gap-2 text-xs font-bold text-indigo-900">
                          <Plus className="w-4 h-4 text-indigo-600" />
                          <span>Tambah Ukuran Kustom Baru</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                          <div className="sm:col-span-4">
                            <label className="text-[11px] font-medium text-slate-600 block mb-1">Lebar (mm)</label>
                            <input
                              type="number"
                              min="10"
                              max="210"
                              value={customWidth}
                              onChange={(e) => setCustomWidth(Number(e.target.value) || 10)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div className="sm:col-span-4">
                            <label className="text-[11px] font-medium text-slate-600 block mb-1">Tinggi (mm)</label>
                            <input
                              type="number"
                              min="10"
                              max="300"
                              value={customHeight}
                              onChange={(e) => setCustomHeight(Number(e.target.value) || 10)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div className="sm:col-span-4">
                            <label className="text-[11px] font-medium text-slate-600 block mb-1">Nama Label (Opsional)</label>
                            <input
                              type="text"
                              value={customLabelName}
                              onChange={(e) => setCustomLabelName(e.target.value)}
                              placeholder="Label Rak / Pakaian"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs bg-white focus:outline-none focus:border-indigo-500 placeholder:text-slate-400"
                            />
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={handleAddCustomSize}
                          className="w-full flex items-center justify-center gap-1.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>Simpan & Gunakan Ukuran Kustom Ini</span>
                        </button>
                      </div>

                      {/* Daftar Ukuran Kustom Tersimpan Akun Ini */}
                      <div>
                        <span className="text-xs font-bold text-slate-700 block mb-2">
                          Daftar Ukuran Kustom Akun Ini ({customSizesList.length})
                        </span>
                        {customSizesList.length === 0 ? (
                          <p className="text-xs text-slate-400 bg-slate-50 p-4 rounded-xl border border-dashed border-slate-200 text-center">
                            Belum ada ukuran kustom yang disimpan untuk akun ini.
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {customSizesList.map((cItem) => {
                              const isActive = isCustomSize && selectedSizeId === cItem.id;
                              return (
                                <div
                                  key={cItem.id}
                                  onClick={() => handleSelectCustomSize(cItem)}
                                  className={`relative p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                                    isActive
                                      ? "border-indigo-500 bg-indigo-50/50 text-indigo-900 ring-2 ring-indigo-500/20 shadow-sm"
                                      : "border-slate-200 bg-white hover:border-slate-300 text-slate-700"
                                  }`}
                                >
                                  <div className="truncate pr-2">
                                    <div className="text-xs font-bold truncate">{cItem.name || cItem.label}</div>
                                    <div className="text-[11px] text-indigo-600 font-semibold">
                                      {cItem.width} × {cItem.height} mm
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {isActive && (
                                      <span className="text-[10px] bg-indigo-600 text-white px-2 py-0.5 rounded-full font-bold">
                                        Aktif
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => handleDeleteCustomSize(cItem.id, e)}
                                      title="Hapus ukuran kustom ini"
                                      className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Informasi pada Label (Toggles) */}
                <div>
                  <label className="block text-[13px] font-bold text-slate-800 mb-3">
                    Informasi pada Label
                  </label>
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                    <div className="flex items-center justify-between px-4 py-3.5">
                      <span className="text-sm font-semibold text-slate-700">Nama Produk</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateDisplayFields((p) => ({ ...p, show_product_name: !p.show_product_name }))
                        }
                        className={`relative inline-flex h-[22px] w-[42px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          displayFields.show_product_name ? "bg-teal-500" : "bg-slate-300"
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 mt-[1px] ml-[1px] transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            displayFields.show_product_name ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between px-4 py-3.5">
                      <span className="text-sm font-semibold text-slate-700">Harga Produk</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateDisplayFields((p) => ({ ...p, show_price: !p.show_price }))
                        }
                        className={`relative inline-flex h-[22px] w-[42px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          displayFields.show_price ? "bg-teal-500" : "bg-slate-300"
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 mt-[1px] ml-[1px] transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            displayFields.show_price ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between px-4 py-3.5">
                      <span className="text-sm font-semibold text-slate-700">Kode SKU</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateDisplayFields((p) => ({ ...p, show_sku: !p.show_sku }))
                        }
                        className={`relative inline-flex h-[22px] w-[42px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          displayFields.show_sku ? "bg-teal-500" : "bg-slate-300"
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 mt-[1px] ml-[1px] transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            displayFields.show_sku ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between px-4 py-3.5">
                      <span className="text-sm font-semibold text-slate-700">Barcode</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateDisplayFields((p) => ({ ...p, show_barcode: !p.show_barcode }))
                        }
                        className={`relative inline-flex h-[22px] w-[42px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          displayFields.show_barcode ? "bg-teal-500" : "bg-slate-300"
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 mt-[1px] ml-[1px] transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            displayFields.show_barcode ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Preview Panel Right Side - Akurat & Diperbesar Realistis */}
              <div className="lg:col-span-5 flex flex-col">
                <div className="bg-slate-50/80 rounded-2xl p-6 border border-slate-200 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-[13px] font-bold text-slate-800">Pratinjau Label Realistis</h3>
                        <p className="text-[11px] text-slate-400">Rata tengah & proporsi cetak presisi</p>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold bg-teal-100 text-teal-800 border border-teal-200">
                        {activeSizeObj.label}
                      </span>
                    </div>

                    {/* Kotak Preview Dinamis dengan Skala Proporsional Otomatis */}
                    <div className="my-4 flex items-center justify-center p-6 bg-slate-100/70 rounded-2xl border border-dashed border-slate-300 min-h-[190px] overflow-visible">
                      <div
                        className="shadow-md rounded-[2mm] bg-white transition-transform duration-150"
                        style={{
                          transform: `scale(${previewScale})`,
                          transformOrigin: "center center",
                        }}
                      >
                        <LabelCard
                          prod={{
                            name: "Butterscotch Memo (Aqua)",
                            price: 12000,
                            sku: "MEMO-01",
                            barcode: "8991234567891",
                          }}
                          fields={displayFields}
                          cardW={activeSizeObj.width}
                          cardH={activeSizeObj.height}
                          isThermal={mediaCetak === "thermal"}
                        />
                      </div>
                    </div>

                    <p className="text-[11px] font-medium text-slate-500 text-center leading-relaxed">
                      Tampilan diskalakan otomatis ({previewScale.toFixed(1)}x) agar pas di layar. Ukuran fisik hasil cetak adalah <strong>{activeSizeObj.label}</strong>.
                    </p>
                  </div>

                  <div className="mt-6 p-3 bg-[#f0f7fd] border border-blue-100 rounded-xl flex items-start gap-3">
                    <div className="p-1.5 bg-blue-500 text-white rounded-full mt-0.5 flex-shrink-0">
                      <Info className="w-3 h-3" />
                    </div>
                    <p className="text-xs text-blue-800 font-medium leading-snug">
                      Pilihan produk dan jumlah salinan label ditentukan pada langkah berikutnya.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: PILIH PRODUK (Gambar 3 - Persis Mockup) */}
          {step === 2 && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Product Table Left Side */}
              <div className="lg:col-span-8 space-y-4">
                {/* Search Bar & Filter Button */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Cari nama produk, varian, SKU, atau barcode..."
                      className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  {itemsWithoutBarcode.length > 0 && (
                    <button
                      onClick={() => setConfirmBulkGenerate(true)}
                      disabled={bulkGenerateState.active}
                      title="Buatkan barcode untuk semua item yang belum punya"
                      className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                        bulkGenerateState.active
                          ? "border-amber-300 text-amber-700 bg-amber-50 cursor-wait"
                          : "border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100"
                      }`}
                    >
                      {bulkGenerateState.active ? (
                        <>
                          <span className="inline-block w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                          Membuat {bulkGenerateState.done}/{bulkGenerateState.total}
                        </>
                      ) : (
                        <>
                          <Plus className="w-3.5 h-3.5" />
                          Generate Semua ({itemsWithoutBarcode.length})
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => setShowFilterPanel((v) => !v)}
                    className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${
                      showFilterPanel || filterCategoryId || filterOnlyBarcode
                        ? "border-teal-500 text-teal-700 bg-teal-50"
                        : "border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <Filter className="w-3.5 h-3.5" />
                    <span>Filter</span>
                    {(filterCategoryId || filterOnlyBarcode) && (
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                    )}
                  </button>
                </div>

                {/* Filter Panel */}
                {showFilterPanel && (
                  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                          Kategori
                        </label>
                        <SearchableSelect
                          options={[{ id: "", name: "Semua Kategori" }, ...categories]}
                          value={filterCategoryId}
                          onChange={(value) => setFilterCategoryId(value)}
                          placeholder="Semua Kategori"
                          displayKey="name"
                          valueKey="id"
                          searchable
                          size="sm"
                          dropUp={false}
                          maxOptionsHeight="max-h-40"
                        />
                      </div>
                      <div className="flex items-end pb-2">
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-700">
                          <input
                            type="checkbox"
                            checked={filterOnlyBarcode}
                            onChange={(e) => setFilterOnlyBarcode(e.target.checked)}
                            className="rounded text-teal-600 focus:ring-teal-500 cursor-pointer"
                          />
                          Hanya item yang sudah punya barcode
                        </label>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                      <p className="text-[11px] text-slate-400">
                        {filteredProductRows.length} item ditemukan
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setFilterCategoryId("");
                            setFilterOnlyBarcode(false);
                          }}
                          className="px-3 py-1.5 border border-slate-300 rounded-lg text-[11px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                        >
                          Reset
                        </button>
                        <button
                          onClick={() => setShowFilterPanel(false)}
                          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-[11px] font-semibold hover:bg-teal-700 transition-colors"
                        >
                          Terapkan
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Table Container */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-slate-700 text-xs font-semibold border-b border-slate-200">
                        <th className="p-3 w-10 text-center">
                          <input
                            type="checkbox"
                            checked={isAllProductsSelected}
                            onChange={toggleSelectAllProducts}
                            disabled={selectableProductRows.length === 0}
                            title="Pilih semua produk di seluruh halaman"
                            aria-label="Pilih semua produk di seluruh halaman"
                            className="rounded text-teal-600 focus:ring-teal-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                          />
                        </th>
                        <th className="p-3 font-semibold">Produk</th>
                        <th className="p-3 font-semibold">SKU / Barcode</th>
                        <th className="p-3 font-semibold">Harga</th>
                        <th className="p-3 font-semibold text-center w-36">Jumlah Label</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                      {loadingProducts ? (
                        <tr>
                          <td colSpan="5" className="py-8 text-center text-slate-400">
                            Memuat daftar produk...
                          </td>
                        </tr>
                      ) : visibleProducts.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="py-10 text-center">
                            <div className="text-slate-400 text-xs font-medium">
                              Tidak ada produk yang cocok
                            </div>
                            <div className="text-[11px] text-slate-300 mt-1">
                              Coba ubah kata kunci atau filter
                            </div>
                          </td>
                        </tr>
                      ) : (
                        visibleProducts.map((prod) => {
                          const itemState = selectedProductMap[prod.id];
                          const isChecked = Boolean(itemState?.selected);
                          const qty = itemState?.qty || 1;
                          const hasBarcode = Boolean(prod.barcode);
                          const isGenerating = generatingBarcodeId === prod.id;

                          return (
                            <tr
                              key={prod.id}
                              className={`transition-colors ${
                                isChecked ? "bg-teal-50/20" : "hover:bg-slate-50"
                              }`}
                            >
                              <td className="p-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={!hasBarcode}
                                  onChange={() => toggleSelectProduct(prod)}
                                  className="rounded text-teal-600 focus:ring-teal-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                                />
                              </td>

                              <td className="p-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {prod.image_url ? (
                                      <img src={prod.image_url} alt={prod.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <Package className="w-4 h-4 text-slate-400" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    {prod.type === "variant" ? (
                                      <div className="leading-tight">
                                        <div className="text-[10px] font-medium text-slate-500 truncate">
                                          {prod.parentName || ""}
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                          <span className="font-semibold text-slate-800 truncate">
                                            {prod.name}
                                          </span>
                                          <span className="flex-shrink-0 text-[9px] font-bold tracking-wide bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-md">
                                            {prod.variantName ? `Varian: ${prod.variantName}` : "Varian"}
                                          </span>
                                        </div>
                                      </div>
                                    ) : prod.type === "unit" ? (
                                      <div className="leading-tight">
                                        <div className="text-[10px] font-medium text-slate-500 truncate">
                                          {prod.parentName || ""}
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                          <span className="font-semibold text-slate-800 truncate">
                                            {prod.name}
                                          </span>
                                          <span className="flex-shrink-0 text-[9px] font-bold tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-md">
                                            Satuan: {prod.unitName}
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1.5">
                                        <span className="font-semibold text-slate-800 line-clamp-1">
                                          {prod.name}
                                        </span>
                                      </div>
                                    )}
                                    {!hasBarcode && (
                                      <div className="text-[10px] text-amber-600 font-medium mt-0.5">
                                        Belum ada barcode
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td className="p-3">
                                {hasBarcode ? (
                                  <div className="space-y-0.5">
                                    <div className="font-mono text-slate-700 text-[11px] font-semibold">
                                      {prod.sku || "-"}
                                    </div>
                                    <div className="w-24">
                                      <BarcodeSVG
                                        value={prod.barcode}
                                        height={16}
                                        width={0.9}
                                        showText={false}
                                        maxWidth={96}
                                      />
                                    </div>
                                    <div className="font-mono text-[10px] text-slate-400">
                                      {prod.barcode}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-start gap-1.5">
                                    <div className="font-mono text-slate-400 text-[11px]">
                                      {prod.sku || "-"}
                                    </div>
                                    <button
                                      onClick={() => handleGenerateBarcode(prod)}
                                      disabled={isGenerating}
                                      title="Buatkan barcode EAN-13 untuk item ini"
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-wait whitespace-nowrap"
                                    >
                                      <Plus className="w-3 h-3 flex-shrink-0" />
                                      {isGenerating ? "Membuat..." : "Generate"}
                                    </button>
                                  </div>
                                )}
                              </td>

                              <td className="p-3 font-semibold text-slate-900">
                                Rp{prod.price.toLocaleString("id-ID")}
                              </td>

                              <td className="p-3 text-center">
                                <div
                                  className={`inline-flex items-center border rounded-lg overflow-hidden bg-white shadow-sm transition-opacity ${
                                    isChecked ? "border-slate-300 opacity-100" : "border-slate-200 opacity-50"
                                  }`}
                                >
                                  <button
                                    onClick={() => isChecked && updateProductQty(prod, -1)}
                                    disabled={!isChecked}
                                    className="px-2.5 py-1 text-slate-600 hover:bg-slate-100 transition-colors border-r border-slate-200 font-semibold disabled:cursor-not-allowed"
                                  >
                                    -
                                  </button>
                                  <span className="px-3 py-1 font-semibold text-slate-800 text-xs min-w-[28px] text-center">
                                    {qty}
                                  </span>
                                  <button
                                    onClick={() => isChecked && updateProductQty(prod, 1)}
                                    disabled={!isChecked}
                                    className="px-2.5 py-1 text-slate-600 hover:bg-slate-100 transition-colors border-l border-slate-200 font-semibold disabled:cursor-not-allowed"
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Product Pagination Bar */}
                {(() => {
                  const totalCount = filteredProductRows.length;
                  const totalPages = Math.max(1, Math.ceil(totalCount / PRODUCT_PAGE_SIZE));
                  const pages = [];
                  const add = (p) => {
                    if (p >= 1 && p <= totalPages && !pages.includes(p)) pages.push(p);
                  };
                  add(1);
                  add(productPage - 1);
                  add(productPage);
                  add(productPage + 1);
                  add(totalPages);
                  pages.sort((a, b) => a - b);
                  const withEllipsis = [];
                  let prev = 0;
                  pages.forEach((p) => {
                    if (prev && p - prev > 1) withEllipsis.push("...");
                    withEllipsis.push(p);
                    prev = p;
                  });
                  return (
                    <div className="flex flex-col items-center gap-1.5 pt-2">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setProductPage(Math.max(1, productPage - 1))}
                          disabled={productPage <= 1}
                          className="p-1.5 border border-slate-300 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                        >
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        {withEllipsis.map((item, i) =>
                          item === "..." ? (
                            <span key={`e${i}`} className="px-1 text-slate-400 text-xs">
                              ...
                            </span>
                          ) : (
                            <button
                              key={item}
                              onClick={() => setProductPage(item)}
                              className={`px-3 py-1 border rounded-lg text-xs font-semibold ${
                                productPage === item
                                  ? "bg-teal-600 text-white border-teal-600"
                                  : "border-slate-300 text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              {item}
                            </button>
                          )
                        )}
                        <button
                          onClick={() => setProductPage(Math.min(totalPages, productPage + 1))}
                          disabled={productPage >= totalPages}
                          className="p-1.5 border border-slate-300 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="text-[11px] text-slate-400">
                        Menampilkan {Math.min(PRODUCT_PAGE_SIZE, totalCount) || 0} dari {totalCount} item per halaman
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Label Summary Panel (Right Side - Gambar 3) */}
              <div className="lg:col-span-4 flex flex-col">
                <div className="bg-slate-50/70 rounded-xl p-5 border border-slate-200 space-y-5">
                  <h3 className="text-[13px] font-bold text-slate-800">Ringkasan Label</h3>

                  <div className="space-y-3 text-xs">
                    <div className="flex items-center justify-between text-slate-500 font-medium">
                      <span>Nama Label</span>
                      <span className="font-semibold text-slate-800 truncate max-w-[150px]">
                        {labelName || "Label Barcode Pakaian"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-slate-500 font-medium">
                      <span>Ukuran</span>
                      <span className="font-semibold text-slate-800">
                        {activeSizeObj.label}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="text-[12px] font-bold text-slate-800 mb-2.5">
                      Informasi Ditampilkan
                    </div>
                    <div className="flex flex-col gap-2">
                      {displayFields.show_product_name && (
                        <div className="flex items-center gap-2 text-xs font-semibold text-teal-600 bg-teal-50/50 w-fit px-2.5 py-1 rounded-full border border-teal-100">
                          <CheckCircle2 className="w-4 h-4 fill-teal-500 text-white" /> Nama Produk
                        </div>
                      )}
                      {displayFields.show_price && (
                        <div className="flex items-center gap-2 text-xs font-semibold text-teal-600 bg-teal-50/50 w-fit px-2.5 py-1 rounded-full border border-teal-100">
                          <CheckCircle2 className="w-4 h-4 fill-teal-500 text-white" /> Harga Produk
                        </div>
                      )}
                      {displayFields.show_barcode && (
                        <div className="flex items-center gap-2 text-xs font-semibold text-teal-600 bg-teal-50/50 w-fit px-2.5 py-1 rounded-full border border-teal-100">
                          <CheckCircle2 className="w-4 h-4 fill-teal-500 text-white" /> Barcode
                        </div>
                      )}
                      {displayFields.show_sku ? (
                        <div className="flex items-center gap-2 text-xs font-semibold text-teal-600 bg-teal-50/50 w-fit px-2.5 py-1 rounded-full border border-teal-100">
                          <CheckCircle2 className="w-4 h-4 fill-teal-500 text-white" /> Kode SKU
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 bg-slate-100 w-fit px-2.5 py-1 rounded-full border border-slate-200">
                          <MinusCircle className="w-4 h-4 fill-slate-400 text-white" /> Kode SKU: Tidak
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stat Cards */}
                  <div className="space-y-3 pt-2">
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0">
                        <Package className="w-6 h-6" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] text-slate-500 font-bold mb-0.5">Produk Dipilih</span>
                        <span className="text-2xl font-black text-slate-800">{totalProductCount}</span>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0">
                        <Tag className="w-6 h-6" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] text-slate-500 font-bold mb-0.5">Total Label</span>
                        <span className="text-2xl font-black text-slate-800">{totalLabelCount}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-[#f0f7fd] border-none rounded-xl flex items-start gap-3 mt-4">
                    <div className="p-1.5 bg-blue-500 text-white rounded-full mt-0.5 flex-shrink-0">
                      <Info className="w-3 h-3" />
                    </div>
                    <p className="text-xs text-blue-800 font-medium leading-snug">
                      Jumlah label masih dapat diubah sebelum dicetak.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: PREVIEW & CETAK (Gambar 4 - Persis Mockup) */}
          {step === 3 && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Printable Sheet Canvas Preview Panel (Left Side) */}
              <div className="lg:col-span-8 bg-slate-100 p-5 rounded-xl border border-slate-200 flex flex-col min-h-[480px]">
                {/* Header Controls of Panel */}
                <div className="flex items-center justify-between mb-3 text-xs">
                  <h3 className="font-bold text-slate-800 text-sm">Preview Cetak</h3>
                  <div className="flex items-center gap-3">
                    {isThermal && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 font-semibold">
                        Mode Roll {activePaper.width} mm
                      </span>
                    )}
                    <SearchableSelect
                      options={[
                        { value: "100%", label: "Sesuai Panel" },
                        { value: "75%", label: "Zoom 75%" },
                        { value: "50%", label: "Zoom 50%" },
                      ]}
                      value={zoomLevel}
                      onChange={(value) => setZoomLevel(value)}
                      placeholder="Zoom"
                      displayKey="label"
                      valueKey="value"
                      className="w-36"
                      searchable={false}
                      size="sm"
                      dropUp={false}
                    />
                    <span className="text-slate-500 font-medium">
                      Halaman {previewPage} dari {layout.totalPages}
                    </span>
                  </div>
                </div>

                {/* Printable Sheet Preview, skala sesuai ukuran kertas asli */}
                <div
                  ref={previewPanelRef}
                  className="flex-1 flex items-center justify-center p-3 overflow-auto bg-white rounded-xl border border-slate-300/60 min-h-0"
                >
                  {layout.fitsW && layout.fitsH ? (
                    <div
                      className="relative flex-shrink-0"
                      style={{
                        width: layout.paperW * MM_TO_PX * scaleFactor,
                        height: layout.paperH * MM_TO_PX * scaleFactor,
                      }}
                    >
                      <div
                        className="absolute top-0 left-0 bg-white shadow-xl border border-slate-300"
                        style={{
                          width: `${layout.paperW}mm`,
                          height: `${layout.paperH}mm`,
                          boxSizing: "border-box",
                          padding: "0mm",
                          transform: `scale(${scaleFactor})`,
                          transformOrigin: "top left",
                        }}
                      >
                        <LabelSheetContent
                          stickers={currentPageStickers}
                          cols={layout.cols}
                          cardW={layout.cardW}
                          cardH={layout.cardH}
                          gapX={layout.gapX}
                          gapY={layout.gapY}
                          marginX={layout.marginX}
                          marginY={layout.marginY}
                          fields={displayFields}
                          isThermal={isThermal}
                          fixedGridWidth={isThermal && layout.cols === 3 ? layout.paperW : null}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-6 max-w-sm">
                      <Info className="w-6 h-6 mx-auto mb-2" />
                      <p className="text-xs font-semibold mb-1">
                        Ukuran label ({activeSizeObj.label}) lebih besar dari kertas yang dipilih
                      </p>
                      <p className="text-[11px]">
                        Pilih ukuran kertas yang lebih besar atau kecilkan ukuran label.
                      </p>
                    </div>
                  )}
                </div>

                {/* Sheet Pagination */}
                <div className="flex items-center justify-center gap-3 pt-3 border-t border-slate-200/60 text-xs">
                  <button
                    onClick={() => setPreviewPage(Math.max(1, previewPage - 1))}
                    disabled={previewPage <= 1}
                    className="p-1 text-slate-600 hover:text-slate-900 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="font-semibold text-slate-700">
                    {previewPage} / {layout.totalPages}
                  </span>
                  <button
                    onClick={() => setPreviewPage(Math.min(layout.totalPages, previewPage + 1))}
                    disabled={previewPage >= layout.totalPages}
                    className="p-1 text-slate-600 hover:text-slate-900 disabled:opacity-30"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Print Controls Panel Right Side (Gambar 4) */}
              <div className="lg:col-span-4 flex flex-col justify-between bg-slate-50/70 p-6 rounded-xl border border-slate-200 space-y-6">
                <div className="space-y-6">
                  {/* Ringkasan Label */}
                  <div>
                    <h3 className="text-[13px] font-bold text-slate-800 mb-3">Ringkasan Label</h3>
                    <div className="space-y-2.5 text-xs">
                      <div className="flex justify-between text-slate-500 font-medium">
                        <span>Nama Label</span>
                        <strong className="text-slate-800 truncate max-w-[150px] font-semibold">{labelName}</strong>
                      </div>
                      <div className="flex justify-between text-slate-500 font-medium">
                        <span>Ukuran</span>
                        <strong className="text-slate-800 font-semibold">{activeSizeObj.label}</strong>
                      </div>
                      <div className="flex justify-between text-slate-500 font-medium">
                        <span>Media</span>
                        <strong className="text-slate-800 font-semibold">
                          {MEDIA_TYPES.find((m) => m.id === mediaCetak)?.label || "Kertas A4"}
                        </strong>
                      </div>
                      <div className="flex justify-between text-slate-500 font-medium">
                        <span>Kertas</span>
                        <strong className="text-slate-800 font-semibold">{activePaper.label}</strong>
                      </div>
                      <div className="flex justify-between text-slate-500 font-medium">
                        <span>Produk</span>
                        <strong className="text-slate-800 font-semibold">{totalProductCount}</strong>
                      </div>
                      <div className="flex justify-between text-slate-500 font-medium">
                        <span>Total Label</span>
                        <strong className="text-slate-800 font-semibold">{totalLabelCount}</strong>
                      </div>
                      <div className="flex justify-between text-slate-500 font-medium">
                        <span>Total Halaman</span>
                        <strong className="text-slate-800 font-semibold">{layout.totalPages}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="h-[1px] bg-slate-200 w-full" />

                  {/* Pengaturan Cetak */}
                  <div>
                    <h3 className="text-[13px] font-bold text-slate-800 mb-4">Pengaturan Cetak</h3>
                    <div className="space-y-4 text-xs">
                      <div>
                        <label className="block text-slate-500 font-semibold mb-1.5">Media Cetak</label>
                        <SearchableSelect
                          options={MEDIA_TYPES}
                          value={mediaCetak}
                          onChange={(v) => {
                            setMediaCetak(v);
                            if (v === "thermal") {
                               setUkuranKertas((prev) =>
                                 prev.startsWith("roll") ? prev : "roll108"
                               );
                            } else {
                              setUkuranKertas((prev) =>
                                prev.startsWith("roll") ? "a4" : prev
                              );
                            }
                          }}
                          placeholder="Pilih media cetak"
                          displayKey="label"
                          valueKey="id"
                          searchable={false}
                          size="sm"
                          dropUp={false}
                        />
                        <p className="text-[10px] text-slate-400 mt-1.5">
                          {MEDIA_TYPES.find((m) => m.id === mediaCetak)?.desc}
                        </p>
                      </div>

                      <div>
                        <label className="block text-slate-500 font-semibold mb-1.5">Ukuran Kertas</label>
                        <SearchableSelect
                          options={mediaCetak === "thermal" ? ROLL_SIZES : PAPER_SIZES}
                          value={ukuranKertas}
                          onChange={(v) => {
                            setUkuranKertas(v);
                            if (v.startsWith("roll")) {
                              setMediaCetak("thermal");
                            } else if (mediaCetak === "thermal") {
                              setMediaCetak("sheet");
                            }
                          }}
                          placeholder="Pilih ukuran kertas"
                          displayKey="label"
                          valueKey="id"
                          searchable={false}
                          size="sm"
                          dropUp={false}
                        />
                      </div>

                      {!isThermal && (
                        <div>
                          <label className="block text-slate-500 font-semibold mb-1.5">Jumlah Kolom (Per Baris)</label>
                          <SearchableSelect
                            options={[
                              { value: 3, label: "3 Kolom" },
                              { value: 4, label: "4 Kolom" },
                              { value: 5, label: "5 Kolom (Default)" },
                            ]}
                            value={sheetColumns}
                            onChange={(v) => setSheetColumns(Number(v))}
                            placeholder="Pilih jumlah kolom"
                            displayKey="label"
                            valueKey="value"
                            searchable={false}
                            size="sm"
                            dropUp={false}
                          />
                          <p className="text-[10px] text-slate-400 mt-1.5">
                            Fleksibel. Kolom akan otomatis dibatasi maksimal yang muat di kertas ({Math.max(1, Math.floor(activePaper.width / (activeSizeObj.width || 40)))} kolom) agar tata letak tidak buyar.
                          </p>
                        </div>
                      )}

                      <p className="text-[10px] text-slate-400 mt-1.5">
                        {isThermal
                          ? `Strip kontinu lebar ${activePaper.width} mm, ${layout.cols} kolom per halaman cetak`
                          : `${layout.cols} kolom × ${layout.rows} baris = ${layout.capacity} label per lembar`}
                      </p>

                      {isThermal && (
                        <div className="bg-teal-50 border border-teal-200 rounded-lg px-3 py-2.5">
                          <p className="text-[11px] font-semibold text-teal-800">
                            Presisi kolom: margin kiri/kanan {layout.marginX} mm, gap antar label {layout.gapX} mm
                          </p>
                          <p className="text-[10px] text-teal-700 mt-0.5">
                            Total {(layout.cols * layout.cardW + (layout.cols - 1) * layout.gapX + layout.marginX * 2).toFixed(1)} mm dari {activePaper.width} mm lebar kertas — posisi label pas dengan kotak kolom, tidak meleset keluar.
                          </p>
                          <p className="text-[10px] text-teal-700 mt-0.5">
                             Panduan: roll 108 mm memakai label 33 × 15 mm, 3 kolom, gap horizontal dan vertikal 2 mm.
                          </p>
                          <p className="text-[10px] text-teal-700 mt-0.5">
                             Pada label 33 × 15 mm, barcode dipendekkan agar nama produk, SKU, dan harga tetap punya ruang.
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-slate-500 font-semibold mb-1.5">Jumlah Salinan</label>
                        <div className="inline-flex items-center border border-slate-300 rounded-lg overflow-hidden bg-white w-full justify-between">
                          <button
                            onClick={() => setJumlahSalinan(Math.max(1, jumlahSalinan - 1))}
                            className="px-4 py-2.5 text-slate-600 hover:bg-slate-100 font-bold border-r border-slate-200 transition-colors"
                          >
                            -
                          </button>
                          <span className="font-bold text-slate-800">{jumlahSalinan}</span>
                          <button
                            onClick={() => setJumlahSalinan(jumlahSalinan + 1)}
                            className="px-4 py-2.5 text-slate-600 hover:bg-slate-100 font-bold border-l border-slate-200 transition-colors"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="h-[1px] bg-slate-200 w-full" />

                  {/* Informasi Ditampilkan */}
                  <div>
                    <div className="text-[12px] font-bold text-slate-800 mb-2.5">
                      Informasi Ditampilkan
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {displayFields.show_product_name && (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-600">
                          <CheckCircle2 className="w-4 h-4 fill-teal-500 text-white" /> Nama Produk
                        </div>
                      )}
                      {displayFields.show_price && (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-600">
                          <CheckCircle2 className="w-4 h-4 fill-teal-500 text-white" /> Harga Produk
                        </div>
                      )}
                      {displayFields.show_barcode && (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-600">
                          <CheckCircle2 className="w-4 h-4 fill-teal-500 text-white" /> Barcode
                        </div>
                      )}
                      {displayFields.show_sku ? (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-600">
                          <CheckCircle2 className="w-4 h-4 fill-teal-500 text-white" /> Kode SKU
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                          <MinusCircle className="w-4 h-4 fill-slate-300 text-white" /> Kode SKU: Tidak
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="p-3 bg-[#f0f7fd] border-none rounded-xl flex items-start gap-3">
                    <div className="p-1.5 bg-blue-500 text-white rounded-full mt-0.5 flex-shrink-0">
                      <Info className="w-3 h-3" />
                    </div>
                    <p className="text-xs text-blue-800 font-medium leading-snug">
                      Lakukan Cetak Uji untuk memastikan ukuran dan posisi label sudah tepat.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Controls (Matching Gambar 3 & 4) */}
        <div className="px-6 py-3.5 border-t border-slate-200 bg-white flex items-center justify-between sticky bottom-0 z-20">
          {step === 1 ? (
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Batal
            </button>
          ) : (
            <button
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-1 px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span>Kembali</span>
            </button>
          )}

          {step === 1 && (
            <button
              onClick={handleNextStep1}
              disabled={checkingLabelName}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm disabled:opacity-60 disabled:cursor-wait"
            >
              <span>Lanjut Pilih Produk</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          )}

          {step === 2 && (
            <button
              onClick={handleNextStep2}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm"
            >
              <span>Lanjut Preview</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          )}

          {step === 3 && (
            <div className="flex items-center gap-2.5">
              <button
                onClick={handleSaveDraft}
                disabled={savingRecord || directPrinting}
                className="flex items-center gap-1.5 px-3.5 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-wait"
              >
                <Package className="w-3.5 h-3.5 text-slate-500" />
                <span>Simpan Draft</span>
              </button>

              {isThermal ? (
                <>
                  <button
                    onClick={handlePrint}
                    disabled={savingRecord || directPrinting}
                    className="flex items-center gap-1.5 px-3.5 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold transition-colors shadow-sm disabled:opacity-60"
                    title="Cetak via dialog browser standar (Ctrl+P / Simpan PDF)"
                  >
                    <Printer className="w-4 h-4 text-slate-500" />
                    <span>Cetak via Browser / PDF</span>
                  </button>

                  <button
                    onClick={() => handleDirectTsplPrint()}
                    disabled={savingRecord || directPrinting}
                    className="flex items-center gap-1.5 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm disabled:opacity-60 disabled:cursor-wait"
                  >
                    <Zap className="w-4 h-4 text-amber-300 fill-amber-300" />
                    <span>{directPrinting ? "Mengirim ke Printer..." : "Cetak Langsung TSPL (1-Klik)"}</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={handlePrint}
                  disabled={savingRecord || directPrinting}
                  className="flex items-center gap-1.5 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm disabled:opacity-60 disabled:cursor-wait"
                >
                  <Printer className="w-4 h-4" />
                  <span>Cetak / Simpan PDF</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Lembar cetak semua halaman via portal ke body, dipakai window.print() */}
      {isOpen &&
        step === 3 &&
        createPortal(
          <div className="print-sheets" style={{ display: "none" }}>
            {isThermal ? (
              layout.thermalRows.map((rowStickers, rIndex) => (
                <div
                  key={rIndex}
                  className={`thermal-print-row`}
                  style={{
                    display: isTestPrint && rIndex > 0 ? "none" : "flex",
                    width: `${layout.paperW}mm`,
                    height: `${layout.cardH + layout.gapY}mm`,
                    boxSizing: "border-box",
                    paddingTop: "0mm",
                    paddingBottom: `${layout.gapY}mm`,
                    paddingLeft: `${layout.marginX}mm`,
                    paddingRight: `${layout.marginX}mm`,
                    flexDirection: "row",
                    gap: `${layout.gapX}mm`,
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    background: "#ffffff",
                    pageBreakAfter: "always",
                    breakAfter: "page",
                    pageBreakInside: "avoid",
                    breakInside: "avoid",
                    overflow: "hidden",
                  }}
                >
                  {rowStickers.map((prod, colIdx) => (
                    <div
                      key={`${prod.id}-${colIdx}`}
                      style={{ width: `${layout.cardW}mm`, height: `${layout.cardH}mm`, flexShrink: 0 }}
                    >
                      <LabelCard
                        prod={prod}
                        fields={displayFields}
                        cardW={layout.cardW}
                        cardH={layout.cardH}
                        isThermal={true}
                      />
                    </div>
                  ))}
                </div>
              ))
            ) : (
              layout.pages.map((pageStickers, pIndex) => (
                <div
                  key={pIndex}
                  className={`printable-sheet print-sheet-p${pIndex}`}
                  style={{
                    display: isTestPrint && pIndex > 0 ? "none" : "block",
                    width: `${layout.paperW}mm`,
                    height: `${layout.pageHeights[pIndex]}mm`,
                    boxSizing: "border-box",
                    padding: "0mm",
                    background: "#ffffff",
                    pageBreakInside: "avoid",
                    overflow: "visible",
                  }}
                >
                  <LabelSheetContent
                    stickers={pageStickers}
                    cols={layout.cols}
                    cardW={layout.cardW}
                    cardH={layout.cardH}
                    gapX={layout.gapX}
                    gapY={layout.gapY}
                    marginX={layout.marginX}
                    marginY={layout.marginY}
                    fields={displayFields}
                    isThermal={false}
                  />
                </div>
              ))
            )}
          </div>,
          document.body
        )}


      {/* Modal Pengaturan Koneksi Printer Windows Spooler */}
      {directPrinterModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col p-6 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                  <Printer className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Pilih Printer Windows Spooler</h3>
                  <p className="text-xs text-slate-500">Cetak instan TSPL tanpa ubah ukuran di Windows</p>
                </div>
              </div>
              <button
                onClick={() => setDirectPrinterModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="py-5 space-y-4">
              <div className="p-4 border border-slate-200 rounded-xl bg-slate-50/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Laptop className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-bold text-slate-800">Daftar Printer Terpasang</span>
                  </div>
                  <button
                    onClick={handleOpenPrinterConfigModal}
                    className="text-[11px] text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                </div>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                  Pilih printer label thermal Anda (misalnya <strong>Xprinter XP-420B</strong>) untuk mengirim data cetak TSPL langsung.
                </p>

                {bridgeStatus ? (
                  <div>
                    {loadingWindowsPrinters ? (
                      <div className="flex items-center justify-center py-4 text-xs text-slate-500 gap-2">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" /> Membaca printer Windows...
                      </div>
                    ) : windowsPrintersList.length > 0 ? (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                        {windowsPrintersList.map((pName) => {
                          const isSelected = savedDirectPrinter?.name === pName;
                          return (
                            <button
                              key={pName}
                              onClick={() => handleSelectWindowsSpooler(pName)}
                              className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all text-left border ${
                                isSelected
                                  ? "bg-indigo-50 border-indigo-500 text-indigo-900 shadow-sm"
                                  : "bg-white border-slate-200 hover:border-indigo-300 hover:bg-slate-50 text-slate-700"
                              }`}
                            >
                              <div className="flex items-center gap-2.5 truncate">
                                <Printer className={`w-4 h-4 ${isSelected ? "text-indigo-600" : "text-slate-400"}`} />
                                <span className="truncate">{pName}</span>
                              </div>
                              {isSelected ? (
                                <span className="text-[10px] bg-indigo-600 text-white px-2 py-0.5 rounded-full">Aktif</span>
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                        Tidak ada printer Windows yang terdeteksi.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-1.5">
                    <p className="text-xs font-bold text-amber-800">Helper Spooler Belum Berjalan</p>
                    <p className="text-[11px] text-amber-700 leading-relaxed">
                      Jalankan file <code>asalink-printer-bridge.exe</code> di backend atau ketik <code>task printer-bridge</code> di terminal untuk menghubungkan helper spooler lokal.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setDirectPrinterModalOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Konfirmasi generate barcode massal */}
      <ConfirmationModal
        isOpen={confirmBulkGenerate}
        onClose={() => setConfirmBulkGenerate(false)}
        onConfirm={handleBulkGenerateBarcode}
        title="Generate Barcode Semua Item?"
        message={`Akan dibuatkan barcode EAN-13 (13 digit) untuk ${itemsWithoutBarcode.length} item yang belum punya barcode. Angka dibuat acak dan dijamin tidak ada yang sama di tenant ini.`}
        confirmText="Generate Sekarang"
        isLoading={bulkGenerateState.active}
      />
    </>
  );
}

// ─── Quick Print ─────────────────────────────────────────────────────────
// Print langsung dari daftar label tersimpan tanpa membuka modal popup.
// Komponen ini tidak menampilkan apa pun di layar, hanya merender lembar
// cetak tersembunyi ke body lalu langsung memanggil window.print().

export function BarcodeLabelQuickPrint({ record, onDone }) {
  const [printed, setPrinted] = useState(false);

  const savedRecordSettings = record?.id ? getBarcodeLabelSettings(record.id) : null;
  const persistedSettings = savedRecordSettings || record?.print_settings || {};

  const displayFields = {
    ...DEFAULT_DISPLAY_FIELDS,
    ...(record?.display_fields || {}),
    ...(persistedSettings.display_fields || {}),
  };

  const mediaCetak = persistedSettings.media_cetak || "thermal";
  const ukuranKertas = persistedSettings.ukuran_kertas || "roll108";
  const jumlahSalinan = Math.max(1, Number(persistedSettings.jumlah_salinan) || 1);
  const sheetColumns = Math.max(3, Number(persistedSettings.sheet_columns) || 5);
  const isThermal = mediaCetak === "thermal";

  const sizeObj = useMemo(() => {
    const sizeStr = String(record?.size || "33x15");
    const dimensions = sizeStr.match(/(\d+(?:\.\d+)?)\s*[×xX]\s*(\d+(?:\.\d+)?)/);
    const matchingPreset = PRESET_SIZES.find(
      (p) => sizeStr.includes(String(p.width)) && sizeStr.includes(String(p.height)),
    );
    if (persistedSettings.is_custom_size || (!matchingPreset && dimensions)) {
      return {
        width: Number(persistedSettings.custom_width || dimensions?.[1] || 33),
        height: Number(persistedSettings.custom_height || dimensions?.[2] || 15),
      };
    }
    return matchingPreset || PRESET_SIZES.find((s) => s.id === "33x15");
  }, [record, persistedSettings]);

  const activePaper = useMemo(() => {
    if (isThermal) {
      return ROLL_SIZES.find((s) => s.id === ukuranKertas) || ROLL_SIZES[0];
    }
    return PAPER_SIZES.find((s) => s.id === ukuranKertas) || PAPER_SIZES[0];
  }, [isThermal, ukuranKertas]);

  const allStickersList = useMemo(() => {
    const list = [];
    (record?.items || []).forEach((it) => {
      const data = {
        id: it.unit_id
          ? `unit_${it.product_id}_${it.product_variant_id || "base"}_${it.unit_id}`
          : it.product_variant_id || it.product_id || it.id,
        type: it.unit_id ? "unit" : it.product_variant_id ? "variant" : "product",
        productId: it.product_id,
        unitId: it.unit_id || null,
        unitName: it.unit_name || "",
        name: it.name || it.product_name || "Produk",
        parentName: it.name || it.product_name || "Produk",
        variantName: "",
        sku: it.sku || "",
        barcode: it.barcode || "",
        price: it.price || it.sell_price || 0,
      };
      for (let k = 0; k < (it.qty || 1) * jumlahSalinan; k++) {
        list.push({ ...data });
      }
    });
    return list;
  }, [record, jumlahSalinan]);

  const layout = useMemo(() => {
    const cardW = sizeObj.width || 33;
    const cardH = sizeObj.height || 15;
    const paperW = activePaper.width;
    const maxCols = Math.max(1, Math.floor(paperW / cardW));

    let cols = isThermal ? maxCols : Math.min(sheetColumns, maxCols);
    let gapX, gapY, marginX, marginY;

    if (isThermal) {
      const isThreeColumnRoll = cardW === 33 && paperW === 108;
      const effectivePaperW = (cardW === 38 && paperW === 80) ? 77.0 : paperW;
      gapX = isThreeColumnRoll ? 2.0 : cols > 1 ? 0.0 : 2.0;
      gapY = 2.0;
      marginY = isThreeColumnRoll ? 0 : 0.5;
      while (cols > 1 && cols * cardW + (cols - 1) * gapX > effectivePaperW) cols -= 1;
      marginX = isThreeColumnRoll
        ? 0
        : cols > 1 ? Math.max(0, (effectivePaperW - (cols * cardW + (cols - 1) * gapX)) / 2) : Math.max(1, (paperW - cardW) / 2);
    } else {
      marginX = 3;
      marginY = 3;
      const availableW = paperW - (marginX * 2);
      const totalCardW = cols * cardW;
      gapX = cols > 1 ? Math.max(0, (availableW - totalCardW) / (cols - 1)) : 3;
      gapY = 3;
    }

    const printableH = isThermal
      ? MAX_THERMAL_PAGE_H - marginY * 2
      : Math.max(1, activePaper.height - marginY * 2);
    const rows = Math.max(1, Math.floor((printableH + gapY) / (cardH + gapY)));

    const totalCount = allStickersList.length;
    const capacity = isThermal ? Math.max(1, totalCount) : cols * rows;
    const totalPages = Math.max(1, Math.ceil(totalCount / capacity));

    const pages = [];
    const pageHeights = [];
    for (let p = 0; p < totalPages; p++) {
      const stickers = allStickersList.slice(p * capacity, (p + 1) * capacity);
      pages.push(stickers);
      if (isThermal) {
        const filledRows = Math.max(1, Math.ceil(stickers.length / cols));
        pageHeights.push(marginY * 2 + filledRows * cardH + Math.max(0, filledRows - 1) * gapY);
      } else {
        pageHeights.push(activePaper.height);
      }
    }

    const paperH = isThermal ? pageHeights[0] : activePaper.height;

    return {
      cardW,
      cardH,
      paperW,
      paperH,
      pageHeights,
      cols,
      gapX,
      gapY,
      marginX,
      marginY,
      pages,
    };
  }, [sizeObj, activePaper, isThermal, sheetColumns, allStickersList]);

  useEffect(() => {
    if (printed || !record) return;
    const timer = setTimeout(() => {
      setPrinted(true);
      window.print();
      if (record?.id) {
        barcodeLabelService.updateStatus(record.id, "Dicetak").catch(() => {});
      }
      if (onDone) onDone();
    }, 150);
    return () => clearTimeout(timer);
  }, [printed, record, onDone]);

  if (!record) return null;

  return (
    <>
      <style>{`
        ${
          isThermal
            ? `@page { size: ${layout.paperW}mm ${layout.cardH + layout.gapY}mm; margin: 0mm; }`
            : layout.pageHeights
                .map((h, i) => `@page sheet-p${i} { size: ${layout.paperW}mm ${h}mm; margin: 0mm; }`)
                .join("\n")
        }
        ${
          !isThermal
            ? layout.pageHeights
                .map((_, i) => `.print-sheet-p${i} { page: sheet-p${i}; }`)
                .join("\n")
            : ""
        }
        @media print {
          html, body {
            margin: 0mm !important;
            padding: 0mm !important;
            background: white !important;
          }
          body > *:not(.print-sheets) {
            display: none !important;
          }
          .print-sheets {
            display: block !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            background: white !important;
          }
          .printable-sheet {
            break-after: ${isThermal ? "always" : "page"} !important;
            page-break-after: always !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            margin: 0 auto !important;
            background: white !important;
            overflow: hidden !important;
          }
          
          .thermal-print-row {
            width: ${layout.paperW}mm !important;
            height: ${layout.cardH + layout.gapY}mm !important;
            box-sizing: border-box !important;
            padding-top: 0mm !important;
            padding-bottom: ${layout.gapY}mm !important;
            padding-left: ${layout.marginX}mm !important;
            padding-right: ${layout.marginX}mm !important;
            display: flex !important;
            flex-direction: row !important;
            gap: ${layout.gapX}mm !important;
            justify-content: flex-start !important;
            align-items: flex-start !important;
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            overflow: hidden !important;
            background: white !important;
          }
          .printable-sheet:last-child,
          .thermal-print-row:last-child {
            break-after: auto !important;
            page-break-after: auto !important;
          }
          @page {
            size: ${isThermal ? `${layout.paperW}mm ${layout.cardH + layout.gapY}mm` : `${layout.paperW}mm ${layout.paperH}mm`};
            margin: 0mm !important;
          }
        }
      `}</style>
      {createPortal(
        <div className="print-sheets" style={{ display: "none" }}>
          {isThermal ? (
            layout.thermalRows.map((rowStickers, rIndex) => (
              <div
                key={rIndex}
                className={`thermal-print-row`}
                style={{
                  width: `${layout.paperW}mm`,
                  height: `${layout.cardH + layout.gapY}mm`,
                  boxSizing: "border-box",
                  paddingTop: "0mm",
                  paddingBottom: `${layout.gapY}mm`,
                  paddingLeft: `${layout.marginX}mm`,
                  paddingRight: `${layout.marginX}mm`,
                  flexDirection: "row",
                  gap: `${layout.gapX}mm`,
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  background: "#ffffff",
                  pageBreakAfter: "always",
                  breakAfter: "page",
                  pageBreakInside: "avoid",
                  breakInside: "avoid",
                  overflow: "hidden",
                }}
              >
                {rowStickers.map((prod, colIdx) => (
                  <div
                    key={`${prod.id}-${colIdx}`}
                    style={{ width: `${layout.cardW}mm`, height: `${layout.cardH}mm`, flexShrink: 0 }}
                  >
                    <LabelCard
                      prod={prod}
                      fields={displayFields}
                      cardW={layout.cardW}
                      cardH={layout.cardH}
                      isThermal={true}
                    />
                  </div>
                ))}
              </div>
            ))
          ) : (
            layout.pages.map((pageStickers, pIndex) => (
              <div
                key={pIndex}
                className={`printable-sheet print-sheet-p${pIndex}`}
                style={{
                  width: `${layout.paperW}mm`,
                  height: `${layout.pageHeights[pIndex]}mm`,
                  boxSizing: "border-box",
                  padding: "0mm",
                  background: "#ffffff",
                  pageBreakInside: "avoid",
                  overflow: "visible",
                }}
              >
                <LabelSheetContent
                  stickers={pageStickers}
                  cols={layout.cols}
                  cardW={layout.cardW}
                  cardH={layout.cardH}
                  gapX={layout.gapX}
                  gapY={layout.gapY}
                  marginX={layout.marginX}
                  marginY={layout.marginY}
                  fields={displayFields}
                  isThermal={false}
                />
              </div>
            ))
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
